import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./directordashboard.css";
import "./MonthlyReport.css";
import MonthlyReport from "./UserMonthlyReport";
import ProfilePage from "./ProfilePage";
import {
  FiBarChart2,
  FiCheckCircle,
  FiClock,
  FiPlus,
  FiLock,
  FiAward,
  FiTarget,
  FiUsers,
  FiSettings,
  FiTrendingUp,
  FiBell,
  FiFileText,
  FiChevronLeft,
  FiPhoneCall,
  FiCheckSquare,
} from "react-icons/fi";
import {
  Box,
  Alert,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  Snackbar,
} from "@mui/material";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip as ChartTooltip,
} from "recharts";
import api from "../api/axiosConfig";

import SalesTracker from "./SalesTracker";
import DirectorUserManagement from "./DirectorUserManagement";
import defaultAvatar from "../images/download.png";
import logo1 from "../images/logo.jpg";

const toTitleCase = (str) => {
  if (!str) return "";
  const withSpaces = str.replace(/_/g, " ").replace(/([A-Z])/g, " $1");
  return withSpaces.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  );
};

// Append a short role suffix for display
const labelWithRole = (user = {}) => {
  const name = user.name || user.username || "";
  const role = (user.role || "").toLowerCase();
  switch (role) {
    case "zonal_head":
      return `${name} (ZH)`;
    case "branch_manager":
      return `${name} (BM)`;
    case "team_lead":
      return `${name} (TL)`;
    case "relationship_manager":
      return `${name} (RM)`;
    default:
      return name;
  }
};

const addDisplayNames = (list = []) =>
  (list || []).map((u) => {
    const userId = u.userId || u.id;
    return {
      ...u,
      userId,
      displayName: u.displayName || labelWithRole(u),
    };
  });

const formatDateForInput = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date)) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatNotificationDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date)) return dateString;
  const day = date.getDate();
  const month = date.toLocaleString("en-GB", { month: "short" });
  const year = date.getFullYear();
  return `${day} ${month},${year}`;
};

const isUserOnLeaveOnDate = (user, dateStr) => {
  if (!user || !user.leaveStatus || !user.leaveStartDate) return false;
  const checkDate = new Date(dateStr + "T00:00:00");
  const startDate = new Date(user.leaveStartDate);
  const endDate = user.leaveEndDate ? new Date(user.leaveEndDate) : null;

  checkDate.setHours(0, 0, 0, 0);
  startDate.setHours(0, 0, 0, 0);
  if (endDate) endDate.setHours(0, 0, 0, 0);

  if (endDate) {
    return checkDate >= startDate && checkDate <= endDate;
  }
  return checkDate >= startDate;
};

// Function to check if a date is a government holiday in India
const isIndianGovernmentHoliday = (date, corporateHolidays = []) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  // Check against corporate holidays from API
  const holidayFromAPI = corporateHolidays.find(holiday => {
    const holidayDate = new Date(holiday.date);
    return holidayDate.getFullYear() === year &&
           holidayDate.getMonth() === month &&
           holidayDate.getDate() === day;
  });

  if (holidayFromAPI) {
    return {
      name: holidayFromAPI.name,
      type: holidayFromAPI.type
    };
  }

  // Fallback to basic fixed holidays if API fails (for redundancy)
  const fixedHolidays = [
    { month: 0, day: 1, name: "New Year's Day" },
    { month: 0, day: 26, name: "Republic Day" },
    { month: 7, day: 15, name: "Independence Day" },
    { month: 9, day: 2, name: "Gandhi Jayanti" },
    { month: 11, day: 25, name: "Christmas" },
    { month: 4, day: 1, name: "Labour Day" },
  ];

  const fixedHoliday = fixedHolidays.find(holiday => holiday.month === month && holiday.day === day);
  if (fixedHoliday) {
    return {
      name: fixedHoliday.name,
      type: 'national'
    };
  }

  return null;
};

// Moved outside the component for performance and to prevent stale closures.
const getMetric = (member = {}, key) => {
  if (!member || !key) return 0;
  const dataKey = key === "dailyPoints" ? "aggregatedPoints" : key;
  const value = member[dataKey];
  if (value !== undefined && value !== null) {
    const parsed = Number(value);
    return !Number.isNaN(parsed) ? parsed : 0;
  }
  return 0;
};

const DirectorDashboard = () => {
  const navigate = useNavigate();
  const { section, "*": restOfUrl } = useParams();
  const currentSection = section || "dashboard";

  let targetUserId = "me";
  let entryDateForTracker = formatDateForInput(new Date());

  if (section === "input-for" && restOfUrl) {
    const parts = restOfUrl.split("/");
    targetUserId = parts[0];
    if (parts[1]) entryDateForTracker = parts[1];
  } else if (section === "input" && restOfUrl) {
    entryDateForTracker = restOfUrl;
  }

  // Granular loading and error states for better UX
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isZonalDetailsLoading, setIsZonalDetailsLoading] = useState(false);
  const [isMyPerfLoading, setIsMyPerfLoading] = useState(false);
  const [isDirectorPerfLoading, setIsDirectorPerfLoading] = useState(false);
  const [isDirectorCalendarLoading, setIsDirectorCalendarLoading] =
    useState(false);

  // State variables
  const [dashboardData, setDashboardData] = useState(null);
  const [directorPerformanceData, setDirectorPerformanceData] = useState([]);
  const [directorSubmissionHistory, setDirectorSubmissionHistory] = useState(
    []
  );
  const [zonalHeads, setZonalHeads] = useState([]);
  const [user, setUser] = useState({});
  const [todaysStatus, setTodaysStatus] = useState({});
  const [kpis, setKpis] = useState({});
  const [performanceTrend, setPerformanceTrend] = useState([]);
  const [submissionHistory, setSubmissionHistory] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [pendingLeaveRequests, setPendingLeaveRequests] = useState([]);
  const [approvedLeaveRequests, setApprovedLeaveRequests] = useState([]);
  const [ownLeaveRequests, setOwnLeaveRequests] = useState([]);
  const [directorSubmissions, setDirectorSubmissions] = useState([]);

  // Add monthly KPI data state for current and previous month comparison
  const [monthlyKpiData, setMonthlyKpiData] = useState({
    kpis: {},
    previousMonthKpis: {},
  });

  // Date range states
  const [zonalDetailsStartDate, setZonalDetailsStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 29);
    return d;
  });
  const [zonalDetailsEndDate, setZonalDetailsEndDate] = useState(new Date());
  const [myPerformanceStartDate, setMyPerformanceStartDate] = useState(
    () => new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  const [myPerformanceEndDate, setMyPerformanceEndDate] = useState(
    () => new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
  );
  const [directorPerformanceStartDate, setDirectorPerformanceStartDate] =
    useState(() => new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
  const [directorPerformanceEndDate, setDirectorPerformanceEndDate] = useState(
    new Date()
  );
  const [directorCalendarStartDate, setDirectorCalendarStartDate] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  const [directorCalendarEndDate, setDirectorCalendarEndDate] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
  );

  // UI and control states
  const [dashboardSearchTerm, setDashboardSearchTerm] = useState("");
  const [userName] = useState(localStorage.getItem("name") || "User");
  const userRole = localStorage.getItem("userRole");
  const [selectedField1, setSelectedField1] = useState("dailyPoints");
  const [selectedField2, setSelectedField2] = useState("none");
  const [selectedLeaderboardMetric, setSelectedLeaderboardMetric] =
    useState("aggregatedPoints");
  const [selectedNotificationTab, setSelectedNotificationTab] =
    useState("pending-approvals");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Dialog and form states
  const [leaveRequestDialogOpen, setLeaveRequestDialogOpen] = useState(false);
  const [leaveRejectDialogOpen, setLeaveRejectDialogOpen] = useState(false);
  const [selectedLeaveRequestId, setSelectedLeaveRequestId] = useState(null);
  const [leaveRejectionReason, setLeaveRejectionReason] = useState("");
  const [leaveRevocationReason, setLeaveRevocationReason] = useState("");
  const [leaveRevokeDialogOpen, setLeaveRevokeDialogOpen] = useState(false);
  const [selectedLeaveRevokeRequestId, setSelectedLeaveRevokeRequestId] =
    useState(null);
  const [leaveRequestForm, setLeaveRequestForm] = useState({
    reason: "",
    start_date: "",
    end_date: "",
  });
  const [isOnLeave, setIsOnLeave] = useState(false);
  const [leaveDurationDays, setLeaveDurationDays] = useState(0);
  const [leaveEndDate, setLeaveEndDate] = useState(null);
  const [selectedPerfMetric, setSelectedPerfMetric] = useState("totalPoints");
  const [chartData, setChartData] = useState([]);
  const [showMissingEntryModal, setShowMissingEntryModal] = useState(false);
  const [showZonalHeadsModal, setShowZonalHeadsModal] = useState(false);
  const [showTeamExplorerModal, setShowTeamExplorerModal] = useState(false);
  const [teamExplorerMode, setTeamExplorerMode] = useState("list"); // list | detail
  const [teamExplorerZonalHead, setTeamExplorerZonalHead] = useState(null);
  const [teamExplorerData, setTeamExplorerData] = useState(null);
  const [teamExplorerContext, setTeamExplorerContext] = useState("calendar"); // calendar | charts
  const [isTeamExplorerLoading, setIsTeamExplorerLoading] = useState(false);
  const [directorTeamOverview, setDirectorTeamOverview] = useState({
    zonalHeads: [],
    branchManagers: [],
    teamLeads: [],
    relationshipManagers: [],
  });
  const [isDirectorTeamOverviewLoading, setIsDirectorTeamOverviewLoading] =
    useState(false);
  const [selectedBranchManagerDetail, setSelectedBranchManagerDetail] =
    useState(null);
  const [branchManagerDirectRMs, setBranchManagerDirectRMs] = useState([]);
  const [modalTitle, setModalTitle] = useState("");
  const [modalSubmittedList, setModalSubmittedList] = useState([]);
  const [modalMissingList, setModalMissingList] = useState([]);
  const [modalOnLeaveList, setModalOnLeaveList] = useState([]);
  const [directZonalHeadRMs, setDirectZonalHeadRMs] = useState([]);
  const [selectedZonalHeadForDirectRMs, setSelectedZonalHeadForDirectRMs] =
    useState(null);
  const [directDirectorRMs, setDirectDirectorRMs] = useState([]);

  // Hierarchical calendar state
  const [calendarLevel, setCalendarLevel] = useState("director"); // 'director' | 'zonal_head' | 'branch_manager' | 'team_lead'
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserName, setSelectedUserName] = useState("");
  const [currentLevelUsers, setCurrentLevelUsers] = useState([]);
  const [navigationHistory, setNavigationHistory] = useState([]); // Track navigation path: [{level, userId, userName}]
  const [showCurrentLevelUsersModal, setShowCurrentLevelUsersModal] =
    useState(false);

  // Hierarchical chart state
  const [chartLevel, setChartLevel] = useState("director"); // 'director' | 'zonal_head' | 'branch_manager' | 'team_lead'
  const [chartSelectedUserId, setChartSelectedUserId] = useState(null);
  const [chartSelectedUserName, setChartSelectedUserName] = useState("");
  const [chartSelectedBranchId, setChartSelectedBranchId] = useState(null);
  const [chartNavigationHistory, setChartNavigationHistory] = useState([]); // Track chart navigation path: [{level, userId, userName}]
  const [chartCurrentLevelUsers, setChartCurrentLevelUsers] = useState([]);
  const [showChartCurrentLevelUsersModal, setShowChartCurrentLevelUsersModal] =
    useState(false);
  const [chartBranchManagerMode, setChartBranchManagerMode] =
    useState("default"); // default | direct_rm
  const [chartBranchManagerDirectRMs, setChartBranchManagerDirectRMs] =
    useState([]);
  const [chartZonalMode, setChartZonalMode] = useState("default"); // default | direct_rm | direct_team
  const [chartZonalDirectRMs, setChartZonalDirectRMs] = useState([]);
  const [zonalTeamModalOpen, setZonalTeamModalOpen] = useState(false);
  const [zonalTeamModalData, setZonalTeamModalData] = useState({
    zonalHead: null,
    branchManagers: [],
    teamLeads: [],
    relationshipManagers: [],
  });
  const [zonalTeamModalLoading, setZonalTeamModalLoading] = useState(false);

  // Corporate holidays state
  const [corporateHolidays, setCorporateHolidays] = useState([]);
  const isChartExplorer = teamExplorerContext === "charts";

  const chartFieldOptions = [
    { value: "dailyPoints", label: "Daily Points" },
    { value: "telephoneSalesCalls", label: "Telephone Sales Calls" },
    { value: "faceToFace", label: "Face to Face" },
    { value: "appointmentMade", label: "Appointment Made" },
    { value: "appointmentKept", label: "Appointment Kept" },
    { value: "factFind", label: "Fact Find" },
    { value: "serviceReviews", label: "Service Reviews" },
    { value: "socialCalls", label: "Social Calls" },
    { value: "highIncomeWealthy", label: "High Income / Wealthy" },
    { value: "referrals", label: "Referrals" },
    { value: "closingInterview", label: "Closing Interview" },
    { value: "salesClose", label: "Sales Close" },
    { value: "none", label: "None" },
  ];
  const leaderboardFieldOptions = [
    { value: "aggregatedPoints", label: "Aggregated Points" },
    ...chartFieldOptions.filter((option) => option.value !== "dailyPoints"),
  ];
  const performanceChartMetrics = [
    { value: "totalPoints", label: "Total Points" },
    { value: "telephoneSalesCalls", label: "Telephone Sales Calls" },
    { value: "faceToFace", label: "Face to Face" },
    { value: "appointmentMade", label: "Appointment Made" },
    { value: "appointmentKept", label: "Appointment Kept" },
    { value: "factFind", label: "Fact Find" },
    { value: "serviceReviews", label: "Service Reviews" },
    { value: "socialCalls", label: "Social Calls" },
    { value: "highIncomeWealthy", label: "High Income / Wealthy" },
    { value: "referrals", label: "Referrals" },
    { value: "closingInterview", label: "Closing Interview" },
    { value: "salesClose", label: "Sales Close" },
  ];

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar({ ...snackbar, open: false });
  };

  // --- START: Refactored Data Fetching ---

  // Targeted function to refresh leave and notification data.
  const fetchLeaveAndNotifications = useCallback(async () => {
    await Promise.allSettled([
      api
        .get("/api/users/me/my-leave-requests")
        .then((res) => setOwnLeaveRequests(res.data || [])),
      api
        .get("/api/users/director-subordinate-leave-requests")
        .then((res) => setPendingLeaveRequests(res.data || [])),
      api
        .get("/api/users/director-leave-requests/approved")
        .then((res) => setApprovedLeaveRequests(res.data || [])),
      api
        .get("/api/users/me/notifications")
        .then((res) => setNotifications(res.data || [])),
    ]);
  }, []);

  // Fetch data that doesn't depend on date ranges, called once on mount.
  const fetchInitialStaticData = useCallback(async () => {
    setIsInitialLoading(true);
    await Promise.allSettled([
      api
        .get(`/api/users/me?t=${new Date().getTime()}`)
        .then((res) => {
          const userData = res.data;
          if (userData) {
            const photoPath = userData.profilePhoto || userData.profile_photo;
            let fullUrl = defaultAvatar;
            if (photoPath) {
              if (
                photoPath.startsWith("http://") ||
                photoPath.startsWith("https://")
              ) {
                fullUrl = photoPath;
              } else {
                const serverOrigin = new URL(api.defaults.baseURL).origin;
                const cleanPath = photoPath.replace(/^\/+/, "");

                fullUrl = `${serverOrigin}/${cleanPath}`;
              }
            }
            setUser({ ...userData, profile_photo: fullUrl });
            localStorage.setItem("name", userData.name || userData.username);
            localStorage.setItem("userId", userData.userId);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch user profile for header:", err);
          setUser((prev) => ({ ...prev, profile_photo: defaultAvatar }));
        }),
      api
        .get("/api/users/me/leave-status")
        .then((res) => {
          setIsOnLeave(res.data.leaveStatus);
          if (res.data.leaveStatus && res.data.leaveStartDate) {
            setLeaveDurationDays(
              Math.max(
                1,
                Math.floor(
                  (new Date() - new Date(res.data.leaveStartDate)) / 86400000
                ) + 1
              )
            );
            setLeaveEndDate(res.data.leaveEndDate || null);
          }
        })
        .catch(() => {}),
      fetchLeaveAndNotifications(),
      api
        .get("/api/users/director-branch-submissions")
        .then((res) => setDirectorSubmissions(res.data || []))
        .catch((err) =>
          console.error("Failed to load director submissions", err)
        ),
    ]);
    setIsInitialLoading(false);
  }, [fetchLeaveAndNotifications]);

  // Corporate holidays - disabled until API is available
  // useEffect(() => {
  //   const fetchHolidays = async () => {
  //     try {
  //       const response = await api.get('/api/holidays');
  //       setCorporateHolidays(response.data || []);
  //     } catch (error) {
  //       console.error('Failed to fetch corporate holidays:', error);
  //       setCorporateHolidays([]);
  //     }
  //   };
  //   fetchHolidays();
  // }, []);

  // Initial data load on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      setIsInitialLoading(true);
      try {
        await fetchInitialStaticData();
        if (["dashboard", "input"].includes(currentSection)) {
          // Fetch the static KPIs for the current month
          const today = new Date();
          const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
          const lastDay = new Date(
            today.getFullYear(),
            today.getMonth() + 1,
            0
          );
          const kpiRes = await api.get(
            `/api/users/me/dashboard?startDate=${formatDateForInput(
              firstDay
            )}&endDate=${formatDateForInput(lastDay)}`
          );
          if (kpiRes.data) {
            setMonthlyKpiData({
              kpis: kpiRes.data.kpis,
              previousMonthKpis: kpiRes.data.previousMonthKpis,
            });
          }
        }
      } catch (e) {
        console.error("Error on initial fetch", e);
      } finally {
        setIsInitialLoading(false);
      }
    };
    fetchInitialData();
  }, [fetchInitialStaticData, currentSection]);

  // Fetch Zonal Heads under Director when date range changes
  useEffect(() => {
    setIsZonalDetailsLoading(true);
    api
      .get(
        `/api/users/zonal-heads-by-director?startDate=${formatDateForInput(
          zonalDetailsStartDate
        )}&endDate=${formatDateForInput(zonalDetailsEndDate)}`
      )
      .then((res) => {
        setZonalHeads(res.data || []);
      })
      .catch((err) => {
        console.error("Failed to load Zonal Head details:", err);
        setError("Could not load Zonal Head details.");
      })
      .finally(() => setIsZonalDetailsLoading(false));
  }, [zonalDetailsStartDate, zonalDetailsEndDate]);

  // Fetch "My Performance" data when its date range changes
  useEffect(() => {
    setIsMyPerfLoading(true);
    api
      .get(
        `/api/users/me/dashboard?startDate=${formatDateForInput(
          myPerformanceStartDate
        )}&endDate=${formatDateForInput(
          myPerformanceEndDate
        )}&clientToday=${formatDateForInput(new Date())}`
      )
      .then((res) => {
        setDashboardData(res.data);
        setKpis(res.data.kpis || {});
        setPerformanceTrend(res.data.performanceTrend || []);
        setSubmissionHistory(res.data.submissionHistory || {});
        setTodaysStatus(res.data.todaysStatus || {});
      })
      .catch((err) => console.error("Failed to load My Performance data:", err))
      .finally(() => setIsMyPerfLoading(false));
  }, [myPerformanceStartDate, myPerformanceEndDate]);

  // Fetch Director Performance data when its date range changes
  useEffect(() => {
    setIsDirectorPerfLoading(true);
    api
      .get(
        `/api/users/director-branch-performance?startDate=${formatDateForInput(
          directorPerformanceStartDate
        )}&endDate=${formatDateForInput(directorPerformanceEndDate)}`
      )
      .then((res) => setDirectorPerformanceData(res.data || []))
      .catch((err) =>
        console.error("Failed to load Director Performance data:", err)
      )
      .finally(() => setIsDirectorPerfLoading(false));
  }, [directorPerformanceStartDate, directorPerformanceEndDate]);

  // Fetch Director Submission Calendar data when its date range changes
  useEffect(() => {
    // Only fetch top-level director data when viewing director calendar
    if (calendarLevel !== "director") return;

    setIsDirectorCalendarLoading(true);
    api
      .get(
        `/api/users/director-detailed-submission-history?startDate=${formatDateForInput(
          directorCalendarStartDate
        )}&endDate=${formatDateForInput(directorCalendarEndDate)}`
      )
      .then((res) => {
        const transformedData = (res.data || []).map((entry) => ({
          userId: entry.userId,
          date: entry.date,
          points: entry.points,
        }));
        setDirectorSubmissionHistory(transformedData);
      })
      .catch((err) =>
        console.error("Failed to load Director Submission History:", err)
      )
      .finally(() => setIsDirectorCalendarLoading(false));
  }, [
    directorCalendarStartDate,
    directorCalendarEndDate,
    directorTeamOverview,
    calendarLevel,
  ]);

  // Fetch submission history when calendar level or date range changes
  useEffect(() => {
    if (calendarLevel === "director") {
      // For director level, we already have the data from the existing useEffect
      return;
    }
    fetchCurrentLevelSubmissionHistory();
  }, [
    calendarLevel,
    selectedUserId,
    directorCalendarStartDate,
    directorCalendarEndDate,
    selectedZonalHeadForDirectRMs,
    directZonalHeadRMs,
    directDirectorRMs,
  ]);

  const transformPerformanceData = useCallback((data) => {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item) => ({
      userId: item.userId,
      name: item.name || item.username || "Unknown",
      displayName: labelWithRole(item),
      totalPoints: getMetric(item, "aggregatedPoints"),
      telephoneSalesCalls: getMetric(item, "telephoneSalesCalls"),
      faceToFace: getMetric(item, "faceToFace"),
      appointmentMade: getMetric(item, "appointmentMade"),
      appointmentKept: getMetric(item, "appointmentKept"),
      factFind: getMetric(item, "factFind"),
      serviceReviews: getMetric(item, "serviceReviews"),
      socialCalls: getMetric(item, "socialCalls"),
      highIncomeWealthy: getMetric(item, "highIncomeWealthy"),
      referrals: getMetric(item, "referrals"),
      closingInterview: getMetric(item, "closingInterview"),
      salesClose: getMetric(item, "salesClose"),
      new_leads: getMetric(item, "newLeads"),
    }));
  }, []);

  // Set chart data for director level (include zonal heads + direct BMs/TLs/RMs)
  useEffect(() => {
    if (chartLevel !== "director") return;

    const combinedMap = new Map();

    // Seed with performance data (may include metrics)
    (directorPerformanceData || []).forEach((item) => {
      const userId = item.userId || item.id;
      if (!userId) return;
      combinedMap.set(userId, { ...item });
    });

    // Add zonal heads
    (zonalHeads || [])
      .filter((z) => (z.role || "").toLowerCase() === "zonal_head")
      .forEach((z) => {
        const userId = z.userId || z.id;
        if (!userId) return;
        if (!combinedMap.has(userId)) combinedMap.set(userId, { ...z });
      });

    // Add direct branch managers
    (directorTeamOverview.branchManagers || [])
      .filter(
        (bm) =>
          (bm.reportsToRole || bm.reports_to_role || bm.reportsTo) ===
          "director"
      )
      .forEach((bm) => {
        const userId = bm.userId || bm.id;
        if (!userId) return;
        if (!combinedMap.has(userId))
          combinedMap.set(userId, { ...bm, role: bm.role || "branch_manager" });
      });

    // Add direct team leads
    (directorTeamOverview.teamLeads || [])
      .filter(
        (tl) =>
          (tl.reportsToRole || tl.reports_to_role || tl.reportsTo) ===
          "director"
      )
      .forEach((tl) => {
        const userId = tl.userId || tl.id;
        if (!userId) return;
        if (!combinedMap.has(userId))
          combinedMap.set(userId, { ...tl, role: tl.role || "team_lead" });
      });

    // Add direct relationship managers
    (directorTeamOverview.relationshipManagers || [])
      .filter(
        (rm) =>
          (rm.reportsToRole || rm.reports_to_role || rm.reportsTo) ===
          "director"
      )
      .forEach((rm) => {
        const userId = rm.userId || rm.id;
        if (!userId) return;
        if (!combinedMap.has(userId))
          combinedMap.set(userId, {
            ...rm,
            role: rm.role || "relationship_manager",
          });
      });

    setChartData(transformPerformanceData(Array.from(combinedMap.values())));
  }, [
    chartLevel,
    directorPerformanceData,
    zonalHeads,
    directorTeamOverview,
    transformPerformanceData,
  ]);

  // Optimized lookup for zonal heads
  const zonalHeadsMap = useMemo(() => {
    if (!zonalHeads || zonalHeads.length === 0) return new Map();
    return new Map(
      zonalHeads.map((head) => [Number(head.userId || head.id), head])
    );
  }, [zonalHeads]);

  // Fetch chart data when chart level changes
  useEffect(() => {
    if (chartLevel !== "director") {
      if (
        chartLevel === "branch_manager" &&
        chartBranchManagerMode === "direct_rm" &&
        chartBranchManagerDirectRMs.length > 0
      ) {
        setChartData(transformPerformanceData(chartBranchManagerDirectRMs));
        return;
      }
      if (
        chartLevel === "zonal_head" &&
        (chartZonalMode === "direct_rm" || chartZonalMode === "direct_team") &&
        chartZonalDirectRMs.length > 0
      ) {
        setChartData(transformPerformanceData(chartZonalDirectRMs));
        return;
      }
      // Fetch data for the current chart level
      const fetchChartLevelData = async () => {
        try {
          let endpoint;
          let dateParams = `?startDate=${formatDateForInput(
            directorPerformanceStartDate
          )}&endDate=${formatDateForInput(directorPerformanceEndDate)}`;

          if (chartLevel === "zonal_head") {
            // Fetch branch managers under this zonal head
            endpoint = `/api/users/branch-managers-by-zonal-head?zonalHeadId=${chartSelectedUserId}${dateParams.replace(
              "?",
              "&"
            )}`;
          } else if (chartLevel === "branch_manager") {
            if (chartBranchManagerMode === "direct_rm") {
              endpoint = `/api/users/relationship-managers-by-director?branchManagerId=${chartSelectedUserId}${dateParams.replace(
                "?",
                "&"
              )}`;
            } else {
              // Fetch team leads under this branch manager
              endpoint = `/api/users/team-leads-by-branch-manager?branchManagerId=${chartSelectedUserId}${dateParams.replace(
                "?",
                "&"
              )}`;
            }
          } else if (chartLevel === "team_lead") {
            // Fetch relationship managers under this team lead
            endpoint = `/api/users/rms-by-team-lead/${chartSelectedUserId}${dateParams}`;
          }

          if (endpoint) {
            const response = await api.get(endpoint);
            let data = response.data || [];

            if (chartLevel === "branch_manager" && chartBranchManagerMode === "direct_rm") {
              const targetId =
                chartSelectedBranchId || chartSelectedUserId || null;
              data = (data || [])
                .map((rm) => ({
                  ...rm,
                  userId: rm.userId || rm.user_id || rm.id,
                }))
                .filter((rm) => {
                  const role =
                    rm.reportsToRole || rm.reports_to_role || rm.reportsTo || "";
                  const toId =
                    rm.reportsToId || rm.reports_to_id || rm.reportsTo || null;
                  return (
                    role === "branch_manager" &&
                    targetId !== null &&
                    Number(toId) === Number(targetId)
                  );
                });
              setChartBranchManagerDirectRMs(addDisplayNames(data));
            }

            // Include direct TLs/RMs under a zonal head for chart view
            if (chartLevel === "zonal_head") {
              const zhRecord = zonalHeadsMap.get(Number(chartSelectedUserId));
              const possibleIds = [
                Number(chartSelectedUserId),
                zhRecord?.zonalId ? Number(zhRecord.zonalId) : null,
                zhRecord?.zonal_id ? Number(zhRecord.zonal_id) : null,
              ].filter(Boolean);
              const matchesZonal = (obj = {}) => {
                const role =
                  (obj.reportsToRole ||
                    obj.reports_to_role ||
                    obj.reportsTo) || "";
                const id =
                  obj.reportsToId || obj.reports_to_id || obj.reportsTo || null;
                return (
                  role === "zonal_head" &&
                  id !== null &&
                  possibleIds.some((pid) => Number(id) === Number(pid))
                );
              };

              // Pull direct TLs and RMs for this zonal head even when branch manager API is used
              const [tlRes, rmRes] = await Promise.allSettled([
                api.get(`/api/users/team-leads-by-zonal-head?zonalHeadId=${chartSelectedUserId}`),
                api.get(`/api/users/relationship-managers-by-zonal-head?zonalHeadId=${chartSelectedUserId}`),
              ]);
              const normalize = (arr = []) =>
                (arr || []).map((u) => ({
                  ...u,
                  userId: u.userId || u.user_id || u.id,
                }));

              const directTLsApi =
                tlRes.status === "fulfilled" ? normalize(tlRes.value.data || []) : [];
              const directRMsApi =
                rmRes.status === "fulfilled" ? normalize(rmRes.value.data || []) : [];

              const directTLs =
                directTLsApi.length > 0
                  ? directTLsApi
                  : normalize(directorTeamOverview.teamLeads || []).filter(matchesZonal);
              const directRMs =
                directRMsApi.length > 0
                  ? directRMsApi.filter((rm) => {
                      const role =
                        rm.reportsToRole || rm.reports_to_role || rm.reportsTo || "";
                      return role === "zonal_head";
                    })
                  : normalize(directorTeamOverview.relationshipManagers || []).filter(
                      matchesZonal
                    );

              const combinedMap = new Map();
              data.forEach((d) => {
                const uid = d.userId || d.id;
                if (uid) combinedMap.set(uid, d);
              });
              [...directTLs, ...directRMs].forEach((u) => {
                const uid = u.userId || u.id;
                if (!uid) return;
                if (!combinedMap.has(uid)) combinedMap.set(uid, u);
              });
              data = Array.from(combinedMap.values());
            }

            // Update chart data directly with fetched (and augmented) data
            setChartData(transformPerformanceData(data));
          }
        } catch (error) {
          console.error("Failed to fetch chart level data:", error);
          // Fallback to filtering existing data if API fails
          let fallbackData = [];
          if (chartLevel === "zonal_head") {
            fallbackData = directorPerformanceData.filter(
              (m) => Number(m.zonalHeadId) === Number(chartSelectedUserId)
            );
          } else if (chartLevel === "branch_manager") {
            if (chartBranchManagerMode === "direct_rm") {
              const targetId =
                chartSelectedBranchId || chartSelectedUserId || null;
              fallbackData =
                directorTeamOverview.relationshipManagers?.filter((rm) => {
                  const role =
                    rm.reportsToRole || rm.reports_to_role || rm.reportsTo || "";
                  const id =
                    rm.reportsToId || rm.reports_to_id || rm.reportsTo || null;
                  return (
                    role === "branch_manager" &&
                    targetId !== null &&
                    Number(id) === Number(targetId)
                  );
                }) || [];
              setChartBranchManagerDirectRMs(addDisplayNames(fallbackData));
            } else {
              fallbackData = directorPerformanceData.filter(
                (m) => Number(m.branchManagerId) === Number(chartSelectedUserId)
              );
            }
          } else if (chartLevel === "team_lead") {
            fallbackData = directorPerformanceData.filter(
              (m) => Number(m.teamLeadId) === Number(chartSelectedUserId)
            );
          }
          setChartData(transformPerformanceData(fallbackData));
        }
      };

      fetchChartLevelData();
    }
  }, [
    chartLevel,
    chartSelectedUserId,
    directorPerformanceStartDate,
    directorPerformanceEndDate,
    directorTeamOverview,
    zonalHeadsMap,
    chartSelectedBranchId,
    chartBranchManagerMode,
    chartZonalMode,
    chartZonalDirectRMs,
    transformPerformanceData,
  ]);

  const reloadDataForSection = useCallback(async () => {
    // This function can be used to manually refresh data if needed
    // For now, we rely on the specific useEffects for date changes
  }, []);

  const fetchDirectorTeamOverview = useCallback(async () => {
    setIsDirectorTeamOverviewLoading(true);
    try {
      const [zhRes, bmRes, tlRes, rmRes] = await Promise.all([
        api.get("/api/users/zonal-heads-by-director"),
        api.get("/api/users/branch-managers-by-director"),
        api.get("/api/users/team-leads-by-director"),
        api.get("/api/users/relationship-managers-by-director"),
      ]);
      setDirectorTeamOverview({
        zonalHeads: zhRes.data || [],
        branchManagers: bmRes.data || [],
        teamLeads: tlRes.data || [],
        relationshipManagers: (rmRes.data || []).map((rm) => ({
          ...rm,
          userId: rm.userId || rm.user_id || rm.id, // normalize for calendar matching
        })),
      });
    } catch (error) {
      console.error("Failed to fetch director team overview", error);
      setSnackbar({
        open: true,
        message: "Failed to load team overview",
        severity: "error",
      });
    } finally {
      setIsDirectorTeamOverviewLoading(false);
    }
  }, [setSnackbar]);

  // Preload director team overview so the director calendar can include all direct reports
  useEffect(() => {
    fetchDirectorTeamOverview();
  }, [fetchDirectorTeamOverview]);

  const fetchTeamExplorerData = useCallback(
    async (zonalHead) => {
      if (!zonalHead) return;

      const zonalHeadId =
        zonalHead.userId ||
        zonalHead.id ||
        zonalHead.user_id ||
        zonalHead.zonalId ||
        zonalHead.zonal_id;

      if (!zonalHeadId) {
        setSnackbar({
          open: true,
          message: "Invalid zonal head id",
          severity: "error",
        });
        return;
      }

      setIsTeamExplorerLoading(true);
      try {
        const response = await api.get(
          `/api/users/zonal-head-hierarchy?zonalHeadId=${zonalHeadId}`
        );
        setTeamExplorerData(response.data || {});
        setTeamExplorerZonalHead({ ...zonalHead, userId: zonalHeadId });
        setTeamExplorerMode("detail");
      } catch (error) {
        console.error("Failed to fetch zonal head hierarchy", error);
        setSnackbar({
          open: true,
          message: "Failed to load team details",
          severity: "error",
        });
      } finally {
        setIsTeamExplorerLoading(false);
      }
    },
    [setSnackbar]
  );

  const handleOpenZonalTeamModal = useCallback(
    async (head) => fetchTeamExplorerData(head),
    [fetchTeamExplorerData]
  );

  const handleViewAllRMsForZonal = useCallback(async () => {
    if (!teamExplorerZonalHead) {
      setSnackbar({
        open: true,
        message: "No zonal head selected.",
        severity: "error",
      });
      return;
    }

    setIsTeamExplorerLoading(true);
    try {
      const zhId =
        teamExplorerZonalHead.userId ||
        teamExplorerZonalHead.id ||
        teamExplorerZonalHead.zonalId ||
        teamExplorerZonalHead.zonal_id;
      const zhName =
        teamExplorerZonalHead.name || teamExplorerZonalHead.username || "";

      const rmRes = await api.get(
        `/api/users/relationship-managers-by-director?zonalHeadId=${zhId}`
      );
      const directZhRMs = (rmRes.data || [])
        .map((rm) => ({
          ...rm,
          userId: rm.userId || rm.user_id || rm.id,
        }))
        .filter(
          (rm) =>
            (rm.reportsToRole ||
              rm.reports_to_role ||
              rm.reportsTo ||
              "") === "zonal_head"
        );

      if (isChartExplorer) {
        const normalizedRMs = addDisplayNames(directZhRMs);
        setChartZonalDirectRMs(normalizedRMs);
        setChartZonalMode("direct_rm");
        setChartSelectedUserId(Number(zhId));
        setChartSelectedUserName(zhName);
        setChartLevel("zonal_head");
        setChartNavigationHistory((prev) => [
          ...prev,
          {
            level: "zonal_head",
            userId: Number(zhId),
            userName: zhName,
          },
        ]);
        setChartCurrentLevelUsers(normalizedRMs);
        setChartData(transformPerformanceData(normalizedRMs));
        setShowTeamExplorerModal(false);
        return;
      }

      setDirectZonalHeadRMs(directZhRMs);
      setDirectDirectorRMs([]);
      setSelectedZonalHeadForDirectRMs(zhId);
      setCurrentLevelUsers(addDisplayNames(directZhRMs));
      setCalendarLevel("direct_rm");
      setSelectedUserId("direct_rm");
      setSelectedUserName("Direct RMs");
      setNavigationHistory((prev) => [
        ...prev,
        {
          level: "direct_rm",
          userId: "direct_rm",
          userName: "Direct RMs",
        },
      ]);
      setShowTeamExplorerModal(false);

      // Preload submission history for these direct RMs
      const endpoint = `/api/users/director-detailed-submission-history?startDate=${formatDateForInput(
        directorCalendarStartDate
      )}&endDate=${formatDateForInput(
        directorCalendarEndDate
      )}&zonalHeadId=${zhId}&directRMs=true`;
      const response = await api.get(endpoint);
      const transformedData = (response.data || []).map((entry) => ({
        userId: Number(entry.userId),
        date: entry.date,
        points: entry.points,
      }));
      setDirectorSubmissionHistory(transformedData);
    } catch (error) {
      console.error("Failed to load direct RMs for zonal head", error);
      setSnackbar({
        open: true,
        message: "Failed to load relationship managers for this zonal head",
        severity: "error",
      });
    } finally {
      setIsTeamExplorerLoading(false);
    }
  }, [
    addDisplayNames,
    directorCalendarEndDate,
    directorCalendarStartDate,
    setSnackbar,
    isChartExplorer,
    teamExplorerZonalHead,
    transformPerformanceData,
    setChartCurrentLevelUsers,
    setChartData,
  ]);

  const handleViewZonalHeadTeamCharts = useCallback(async () => {
    if (!isChartExplorer) return;
    if (!teamExplorerZonalHead) {
      setSnackbar({
        open: true,
        message: "No zonal head selected.",
        severity: "error",
      });
      return;
    }

    const zhId =
      teamExplorerZonalHead.userId ||
      teamExplorerZonalHead.id ||
      teamExplorerZonalHead.zonalId ||
      teamExplorerZonalHead.zonal_id;
    const zhName =
      teamExplorerZonalHead.name || teamExplorerZonalHead.username || "";

    const possibleIds = [
      zhId,
      teamExplorerZonalHead.zonalId,
      teamExplorerZonalHead.zonal_id,
    ]
      .filter((v) => v !== undefined && v !== null)
      .map((v) => Number(v));

    const matchesZonal = (obj = {}) => {
      const role =
        obj.reportsToRole || obj.reports_to_role || obj.reportsTo || "";
      const id =
        obj.reportsToId || obj.reports_to_id || obj.reportsTo || obj.zonalId;
      return (
        role === "zonal_head" &&
        id !== undefined &&
        id !== null &&
        possibleIds.includes(Number(id))
      );
    };

    const normalize = (arr = []) =>
      (arr || []).map((u) => ({
        ...u,
        userId: u.userId || u.user_id || u.id,
      }));

    setIsTeamExplorerLoading(true);
    try {
      // Fetch the latest direct reports under the zonal head (tolerant to partial failures)
      const [bmRes, tlRes, rmRes] = await Promise.allSettled([
        api.get(`/api/users/branch-managers-by-zonal-head?zonalHeadId=${zhId}`),
        api.get(`/api/users/team-leads-by-zonal-head?zonalHeadId=${zhId}`),
        api.get(
          `/api/users/relationship-managers-by-zonal-head?zonalHeadId=${zhId}`
        ),
      ]);

      const bmData = bmRes.status === "fulfilled" ? bmRes.value.data || [] : [];
      const tlData = tlRes.status === "fulfilled" ? tlRes.value.data || [] : [];
      const rmData = rmRes.status === "fulfilled" ? rmRes.value.data || [] : [];

      const branchManagers =
        normalize(bmData).length > 0
          ? normalize(bmData)
          : normalize(teamExplorerData?.branchManagers || []).filter(
              matchesZonal
            );

      const directTLs =
        normalize(tlData).length > 0
          ? normalize(tlData)
          : normalize(directorTeamOverview.teamLeads || []).filter(matchesZonal);

      const directRMs = normalize(rmData).filter((rm) => {
        const role =
          rm.reportsToRole || rm.reports_to_role || rm.reportsTo || "";
        return role === "zonal_head";
      });

      let combined = [...branchManagers, ...directTLs, ...directRMs];

      if (combined.length === 0) {
        const fallbackBMs =
          normalize(directorTeamOverview.branchManagers || []).filter(
            matchesZonal
          );
        const fallbackTLs =
          normalize(directorTeamOverview.teamLeads || []).filter(matchesZonal);
        const fallbackRMs =
          normalize(directorTeamOverview.relationshipManagers || []).filter(
            matchesZonal
          );
        combined = [...fallbackBMs, ...fallbackTLs, ...fallbackRMs];
      }

      const normalized = addDisplayNames(combined);
      setChartZonalDirectRMs(normalized);
      setChartZonalMode("direct_team");
      setChartSelectedUserId(Number(zhId));
      setChartSelectedUserName(zhName);
      setChartLevel("zonal_head");
      setChartNavigationHistory((prev) => [
        ...prev,
        {
          level: "zonal_head",
          userId: Number(zhId),
          userName: zhName,
        },
      ]);
      setChartCurrentLevelUsers(normalized);
      setChartData(transformPerformanceData(normalized));
      setShowTeamExplorerModal(false);
    } catch (error) {
      console.error("Failed to load zonal head team charts", error);
      setSnackbar({
        open: true,
        message: "Failed to load zonal head team charts",
        severity: "error",
      });
    } finally {
      setIsTeamExplorerLoading(false);
    }
  }, [
    isChartExplorer,
    teamExplorerZonalHead,
    teamExplorerData,
    directorTeamOverview,
    addDisplayNames,
    transformPerformanceData,
    setChartCurrentLevelUsers,
    setChartData,
    setIsTeamExplorerLoading,
  ]);

  const handleViewDirectRMsForDirector = useCallback(async () => {
    setIsTeamExplorerLoading(true);
    try {
      const rmRes = await api.get(`/api/users/relationship-managers-by-director`);
      const directRMs = (rmRes.data || [])
        .map((rm) => ({
          ...rm,
          userId: rm.userId || rm.user_id || rm.id,
        }))
        .filter(
          (rm) =>
            (rm.reportsToRole || rm.reports_to_role || rm.reportsTo) ===
            "director"
        );

      if (isChartExplorer) {
        const normalized = addDisplayNames(directRMs);
        setChartBranchManagerMode("direct_rm");
        setChartBranchManagerDirectRMs(normalized);
        setChartSelectedBranchId(null);
        setChartSelectedUserId("direct_rm_director");
        setChartSelectedUserName("Direct RMs");
        setChartLevel("branch_manager");
        setChartNavigationHistory((prev) => [
          ...prev,
          {
            level: "branch_manager",
            userId: "direct_rm_director",
            userName: "Direct RMs",
            branchId: null,
          },
        ]);
        setChartCurrentLevelUsers(normalized);
        setChartData(transformPerformanceData(normalized));
        setShowTeamExplorerModal(false);
        return;
      }

      setDirectDirectorRMs(directRMs);
      setSelectedZonalHeadForDirectRMs(null);
      setDirectZonalHeadRMs([]);
      setCurrentLevelUsers(addDisplayNames(directRMs));
      setCalendarLevel("direct_rm");
      setSelectedUserId("direct_rm");
      setSelectedUserName("Direct RMs");
      setNavigationHistory((prev) => [
        ...prev,
        { level: "direct_rm", userId: "direct_rm", userName: "Direct RMs" },
      ]);
      setShowTeamExplorerModal(false);

      const endpoint = `/api/users/director-detailed-submission-history?startDate=${formatDateForInput(
        directorCalendarStartDate
      )}&endDate=${formatDateForInput(
        directorCalendarEndDate
      )}&directRMs=true`;
      const response = await api.get(endpoint);
      const transformedData = (response.data || []).map((entry) => ({
        userId: entry.userId,
        date: entry.date,
        points: entry.points,
      }));
      setDirectorSubmissionHistory(transformedData);
    } catch (error) {
      console.error("Failed to load direct RMs for director", error);
      setSnackbar({
        open: true,
        message: "Failed to load direct relationship managers",
        severity: "error",
      });
    } finally {
      setIsTeamExplorerLoading(false);
    }
  }, [
    addDisplayNames,
    directorCalendarEndDate,
    directorCalendarStartDate,
    setSnackbar,
    setChartCurrentLevelUsers,
    setChartData,
    transformPerformanceData,
    isChartExplorer,
  ]);

// --- END: Refactored Data Fetching ---

// Filter pending leave requests to exclude those already approved
  const filteredPendingLeaveRequests = useMemo(() => {
    const approvedIds = new Set(
      approvedLeaveRequests.map((req) => req.leaveRequestId)
    );
    return pendingLeaveRequests.filter(
      (request) => !approvedIds.has(request.leaveRequestId)
    );
  }, [pendingLeaveRequests, approvedLeaveRequests]);

  // Navigation functions for hierarchical calendar
  const navigateToLevel = useCallback((level, userId = null, userName = "") => {
    const newHistoryEntry = { level, userId, userName };
    setNavigationHistory((prev) => [...prev, newHistoryEntry]);
    setCalendarLevel(level);
    setSelectedUserId(userId);
    setSelectedUserName(userName);
  }, []);

  const navigateBack = useCallback(() => {
    if (navigationHistory.length > 0) {
      const newHistory = [...navigationHistory];
      newHistory.pop(); // Remove current level
      setNavigationHistory(newHistory);

      if (newHistory.length === 0) {
        // Back to director level
        setCalendarLevel("director");
        setSelectedUserId(null);
        setSelectedUserName("");
        setCurrentLevelUsers([]);
      } else {
        // Back to previous level
        const prevEntry = newHistory[newHistory.length - 1];
        setCalendarLevel(prevEntry.level);
        setSelectedUserId(prevEntry.userId);
        setSelectedUserName(prevEntry.userName);
      }
    }
  }, [navigationHistory]);

  // Navigation functions for hierarchical chart
  const navigateChartToLevel = useCallback(
    (level, userId = null, userName = "", branchId = null) => {
      if (level !== "branch_manager" && chartBranchManagerMode !== "default") {
        setChartBranchManagerMode("default");
        setChartBranchManagerDirectRMs([]);
      }
      if (level !== "zonal_head" && chartZonalMode !== "default") {
        setChartZonalMode("default");
        setChartZonalDirectRMs([]);
      }
      if (level === "branch_manager") {
        setChartSelectedBranchId(branchId);
      } else {
        setChartSelectedBranchId(null);
      }
      const newHistoryEntry = { level, userId, userName, branchId };
      setChartNavigationHistory((prev) => [...prev, newHistoryEntry]);
      setChartLevel(level);
      setChartSelectedUserId(userId);
      setChartSelectedUserName(userName);
    },
    [chartBranchManagerMode, chartZonalMode]
  );

  const navigateChartBack = useCallback(() => {
    if (chartNavigationHistory.length > 0) {
      const newHistory = [...chartNavigationHistory];
      newHistory.pop(); // Remove current level
      setChartNavigationHistory(newHistory);

      if (newHistory.length === 0) {
        // Back to director level
        setChartLevel("director");
        setChartSelectedUserId(null);
        setChartSelectedUserName("");
        setChartSelectedBranchId(null);
        setChartCurrentLevelUsers([]);
        setChartBranchManagerMode("default");
        setChartBranchManagerDirectRMs([]);
        setChartZonalMode("default");
        setChartZonalDirectRMs([]);
      } else {
        // Back to previous level
        const prevEntry = newHistory[newHistory.length - 1];
        setChartLevel(prevEntry.level);
        setChartSelectedUserId(prevEntry.userId);
        setChartSelectedUserName(prevEntry.userName);
        setChartSelectedBranchId(
          prevEntry.level === "branch_manager" ? prevEntry.branchId || null : null
        );
        if (prevEntry.level !== "branch_manager") {
          setChartBranchManagerMode("default");
          setChartBranchManagerDirectRMs([]);
        }
        if (prevEntry.level !== "zonal_head") {
          setChartZonalMode("default");
          setChartZonalDirectRMs([]);
        }
      }
    }
  }, [chartNavigationHistory]);

  // Fetch current level users based on calendar level
  const fetchCurrentLevelUsers = useCallback(async () => {
    if (calendarLevel === "director") {
      // Include zonal heads + direct branch managers + direct team leads + direct RMs
      const combined = [
        ...(zonalHeads.filter((m) => m.role === "zonal_head") || []),
        ...(directorTeamOverview.branchManagers || []),
        ...(directorTeamOverview.teamLeads || []),
        ...(directorTeamOverview.relationshipManagers || []),
      ];
      const unique = new Map();
      combined.forEach((u) => {
        const id = u.userId || u.id;
        if (!id) return;
        if (!unique.has(id))
          unique.set(id, { ...u, userId: id, displayName: labelWithRole(u) });
      });
      setCurrentLevelUsers(Array.from(unique.values()));
      return;
    }
    if (calendarLevel === "branch_manager" && selectedUserId) {
      try {
        const res = await api.get(
          `/api/users/team-leads-by-branch-manager?branchManagerId=${selectedUserId}`
        );
        setCurrentLevelUsers(addDisplayNames(res.data || []));
      } catch (error) {
        console.error("Failed to fetch team leads for branch manager:", error);
        setSnackbar({
          open: true,
          message: "Failed to load team leads",
          severity: "error",
        });
      }
      return;
    }
    if (
      (calendarLevel === "branch_manager" || calendarLevel === "team_lead") &&
      !selectedUserId
    ) {
      return;
    }
    if (calendarLevel === "direct_rm") {
      setCurrentLevelUsers(
        addDisplayNames(
          (selectedZonalHeadForDirectRMs && directZonalHeadRMs.length > 0
            ? directZonalHeadRMs
            : directDirectorRMs.length > 0
            ? directDirectorRMs
            : directorTeamOverview.relationshipManagers) || []
        )
      );
      return;
    }
    if (calendarLevel === "bm_direct_rm") {
      setCurrentLevelUsers(addDisplayNames(branchManagerDirectRMs || []));
      return;
    }

    try {
      let response;
      if (calendarLevel === "zonal_head") {
        response = await api.get(
          `/api/users/branch-managers-by-zonal-head?zonalHeadId=${selectedUserId}`
        );
      } else if (calendarLevel === "branch_manager") {
        response = await api.get(
          `/api/users/team-leads-by-branch-manager?branchManagerId=${selectedUserId}`
        );
      } else if (calendarLevel === "team_lead") {
        response = await api.get(
          `/api/users/rms-by-team-lead/${selectedUserId}`
        );
      }

      if (response?.data) {
        const normalized = addDisplayNames(response.data).map((item) => {
          // Normalize reports_to_role casing to avoid mismatches later
          if (item.reports_to_role && !item.reportsToRole) {
            return { ...item, reportsToRole: item.reports_to_role };
          }
          return item;
        });
        setCurrentLevelUsers(normalized);
      }
    } catch (error) {
      console.error("Failed to fetch current level users:", error);
      setSnackbar({
        open: true,
        message: "Failed to load team members",
        severity: "error",
      });
    }
  }, [
    calendarLevel,
    selectedUserId,
    zonalHeads,
    directorTeamOverview,
    selectedZonalHeadForDirectRMs,
    directZonalHeadRMs,
  ]);

  // Fetch submission history for current level
  const fetchCurrentLevelSubmissionHistory = useCallback(async () => {
    if (!selectedUserId || calendarLevel === "director") return;
    if (calendarLevel === "direct_rm") {
      setIsDirectorCalendarLoading(true);
      try {
        const endpoint = `/api/users/director-detailed-submission-history?startDate=${formatDateForInput(
          directorCalendarStartDate
        )}&endDate=${formatDateForInput(directorCalendarEndDate)}${
          selectedZonalHeadForDirectRMs
            ? `&zonalHeadId=${selectedZonalHeadForDirectRMs}`
            : ""
        }&directRMs=true`;
        const response = await api.get(endpoint);
        const transformedData = (response.data || []).map((entry) => ({
          userId: entry.userId,
          date: entry.date,
          points: entry.points,
        }));
        setDirectorSubmissionHistory(transformedData);
        setCurrentLevelUsers(
          addDisplayNames(
            (selectedZonalHeadForDirectRMs && directZonalHeadRMs.length > 0
              ? directZonalHeadRMs
              : selectedZonalHeadForDirectRMs
              ? [] // If filtering by zonal head and none found, keep empty
              : directDirectorRMs.length > 0
              ? directDirectorRMs
              : directorTeamOverview.relationshipManagers) || []
          )
        );
      } catch (error) {
        console.error("Failed to fetch direct RM submission history:", error);
      } finally {
        setIsDirectorCalendarLoading(false);
      }
      return;
    }
    if (calendarLevel === "bm_direct_rm") {
      setIsDirectorCalendarLoading(true);
      try {
        const endpoint = `/api/users/director-detailed-submission-history?startDate=${formatDateForInput(
          directorCalendarStartDate
        )}&endDate=${formatDateForInput(
          directorCalendarEndDate
        )}&branchManagerId=${selectedUserId}&directRMs=true`;
        const response = await api.get(endpoint);
        const transformedData = (response.data || []).map((entry) => ({
          userId: entry.userId,
          date: entry.date,
          points: entry.points,
        }));
        setDirectorSubmissionHistory(transformedData);
      } catch (error) {
        console.error("Failed to fetch branch manager direct RM history:", error);
      } finally {
        setIsDirectorCalendarLoading(false);
      }
      return;
    }

    setIsDirectorCalendarLoading(true);
    try {
      let endpoint;
      if (calendarLevel === "branch_manager") {
        try {
          const tlRes = await api.get(
            `/api/users/team-leads-by-branch-manager?branchManagerId=${selectedUserId}`
          );
          setCurrentLevelUsers(tlRes.data || []);
        } catch (err) {
          console.error("Failed to load team leads for branch manager:", err);
        }
      }
      if (calendarLevel === "zonal_head") {
        endpoint = `/api/users/branch-detailed-submission-history?startDate=${formatDateForInput(
          directorCalendarStartDate
        )}&endDate=${formatDateForInput(
          directorCalendarEndDate
        )}&zonalHeadId=${selectedUserId}`;
      } else if (calendarLevel === "branch_manager") {
        endpoint = `/api/users/branch-detailed-submission-history?startDate=${formatDateForInput(
          directorCalendarStartDate
        )}&endDate=${formatDateForInput(
          directorCalendarEndDate
        )}&branchManagerId=${selectedUserId}`;
      } else if (calendarLevel === "team_lead") {
        endpoint = `/api/users/branch-detailed-submission-history?startDate=${formatDateForInput(
          directorCalendarStartDate
        )}&endDate=${formatDateForInput(
          directorCalendarEndDate
        )}&teamLeadId=${selectedUserId}`;
      }

      const response = await api.get(endpoint);
      const transformedData = (response.data || []).map((entry) => ({
        userId: entry.userId,
        date: entry.date,
        points: entry.points,
      }));
      setDirectorSubmissionHistory(transformedData);
    } catch (error) {
      console.error("Failed to fetch current level submission history:", error);
    } finally {
      setIsDirectorCalendarLoading(false);
    }
  }, [
    calendarLevel,
    selectedUserId,
    directorCalendarStartDate,
    directorCalendarEndDate,
  ]);

  // Fetch chart current level users based on chart level
  const fetchChartCurrentLevelUsers = useCallback(async () => {
    if (chartLevel === "director") {
      // Already have zonal heads from zonalHeads
      setChartCurrentLevelUsers(
        zonalHeads.filter((m) => m.role === "zonal_head")
      );
      return;
    }

    if (chartLevel === "zonal_head") {
      if (chartZonalMode === "direct_rm" || chartZonalMode === "direct_team") {
        setChartCurrentLevelUsers(chartZonalDirectRMs);
        return;
      }
    }

    if (chartLevel === "branch_manager" && chartBranchManagerMode === "direct_rm") {
      setChartCurrentLevelUsers(chartBranchManagerDirectRMs);
      return;
    }

    try {
      let response;
      if (chartLevel === "zonal_head") {
        const zhId = Number(chartSelectedUserId);
        const [bmRes, tlRes, rmRes] = await Promise.allSettled([
          api.get(`/api/users/branch-managers-by-zonal-head?zonalHeadId=${zhId}`),
          api.get(`/api/users/team-leads-by-zonal-head?zonalHeadId=${zhId}`),
          api.get(`/api/users/relationship-managers-by-zonal-head?zonalHeadId=${zhId}`),
        ]);
        const normalize = (arr = []) =>
          (arr || []).map((u) => ({
            ...u,
            userId: u.userId || u.user_id || u.id,
          }));
        const branchManagers =
          bmRes.status === "fulfilled" ? normalize(bmRes.value.data || []) : [];
        const directTLs =
          tlRes.status === "fulfilled" ? normalize(tlRes.value.data || []) : [];
        const directRMs =
          rmRes.status === "fulfilled" ? normalize(rmRes.value.data || []) : [];

        const combinedMap = new Map();
        [...branchManagers, ...directTLs, ...directRMs].forEach((u) => {
          const uid = u.userId || u.id;
          if (!uid) return;
          if (!combinedMap.has(uid)) combinedMap.set(uid, u);
        });
        setChartCurrentLevelUsers(Array.from(combinedMap.values()));
        return;
      }

      if (chartLevel === "branch_manager") {
        response = await api.get(
          `/api/users/team-leads-by-branch-manager?branchManagerId=${chartSelectedUserId}`
        );
      } else if (chartLevel === "team_lead") {
        response = await api.get(
          `/api/users/rms-by-team-lead/${chartSelectedUserId}`
        );
      }

      if (response?.data) {
        setChartCurrentLevelUsers(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch chart current level users:", error);
      setSnackbar({
        open: true,
        message: "Failed to load team members for chart",
        severity: "error",
      });
    }
  }, [
    chartLevel,
    chartSelectedUserId,
    chartSelectedBranchId,
    zonalHeads,
    chartBranchManagerMode,
    chartBranchManagerDirectRMs,
    chartZonalMode,
    chartZonalDirectRMs,
  ]);

  // Fetch current level users when calendar level changes
  useEffect(() => {
    fetchCurrentLevelUsers();
  }, [
    calendarLevel,
    selectedUserId,
    zonalHeads,
    directorTeamOverview,
    selectedZonalHeadForDirectRMs,
    directZonalHeadRMs,
    directDirectorRMs,
  ]);

  // Fetch chart current level users when chart level changes
  useEffect(() => {
    fetchChartCurrentLevelUsers();
  }, [
    chartLevel,
    chartSelectedUserId,
    zonalHeads,
    chartBranchManagerMode,
    chartBranchManagerDirectRMs,
    chartZonalMode,
    chartZonalDirectRMs,
  ]);

  const goToLevel = useCallback(
    (level, userId = null, userName = "", branchId = null) => {
      if (teamExplorerContext === "charts") {
        navigateChartToLevel(level, userId, userName, branchId);
      } else {
        navigateToLevel(level, userId, userName);
      }
    },
    [teamExplorerContext, navigateChartToLevel, navigateToLevel]
  );

  // Allow chart "Explore Team" to reuse the calendar modal experience
  const openChartExploreModal = useCallback(async () => {
    setIsTeamExplorerLoading(true);
    try {
      setTeamExplorerContext("charts");
      setTeamExplorerMode("list");
      setTeamExplorerZonalHead(null);
      setTeamExplorerData(null);
      setChartSelectedBranchId(null);
      setChartBranchManagerMode("default");
      setChartBranchManagerDirectRMs([]);
      setChartZonalMode("default");
      setChartZonalDirectRMs([]);
      await fetchDirectorTeamOverview();
      setShowTeamExplorerModal(true);
    } catch (error) {
      console.error("Failed to open chart explore modal", error);
      setSnackbar({
        open: true,
        message: "Failed to load team data for charts",
        severity: "error",
      });
    } finally {
      setIsTeamExplorerLoading(false);
    }
  }, [
    fetchDirectorTeamOverview,
    setSnackbar,
  ]);

  const labelWithContextSuffix = useCallback(
    (text) => `${text}${teamExplorerContext === "charts" ? " Charts" : ""}`,
    [teamExplorerContext]
  );

  const handleCalendarDateClick = (dateStr) => {
    const submittedEntriesOnDate = directorSubmissionHistory.filter(
      (entry) => entry.date === dateStr
    );
    const submittedUserIds = new Set(
      submittedEntriesOnDate.map((entry) => Number(entry.userId))
    );

    let modalTitleText = "";
    let targetGroup = [];

    if (calendarLevel === "director") {
      modalTitleText = `Team Submissions for ${formatNotificationDate(
        dateStr
      )}`;
      targetGroup = currentLevelUsers;
    } else if (calendarLevel === "zonal_head") {
      modalTitleText = `Branch Manager Submissions for ${formatNotificationDate(
        dateStr
      )}`;
      targetGroup = currentLevelUsers;
    } else if (calendarLevel === "branch_manager") {
      modalTitleText = `Team Lead Submissions for ${formatNotificationDate(
        dateStr
      )}`;
      targetGroup = currentLevelUsers;
    } else if (calendarLevel === "team_lead") {
      modalTitleText = `RM Submissions for ${formatNotificationDate(dateStr)}`;
      targetGroup = currentLevelUsers;
    } else if (calendarLevel === "direct_rm") {
      modalTitleText = `Direct RM Submissions for ${formatNotificationDate(
        dateStr
      )}`;
      targetGroup = currentLevelUsers;
    } else if (calendarLevel === "bm_direct_rm") {
      modalTitleText = `Direct RM Submissions for ${formatNotificationDate(
        dateStr
      )}`;
      targetGroup = currentLevelUsers;
    }

    setModalTitle(modalTitleText);

    const fallbackRoleForLevel = () => {
      if (calendarLevel === "zonal_head") return "branch_manager";
      if (calendarLevel === "branch_manager") return "team_lead";
      if (
        calendarLevel === "team_lead" ||
        calendarLevel === "direct_rm" ||
        calendarLevel === "bm_direct_rm"
      )
        return "relationship_manager";
      return null;
    };

    const buildDisplayName = (member) => {
      const role = member.role || fallbackRoleForLevel();
      if (!role) return member.displayName || member.name || member.username;
      return member.displayName || labelWithRole({ ...member, role });
    };

    const submitted = [];
    const missing = [];
    const onLeave = [];

    targetGroup.forEach((member) => {
      const displayName = buildDisplayName(member);
      if (isUserOnLeaveOnDate(member, dateStr)) {
        onLeave.push({ ...member, displayName });
      } else if (submittedUserIds.has(Number(member.userId))) {
        const submission = submittedEntriesOnDate.find(
          (s) => Number(s.userId) === Number(member.userId)
        );
        submitted.push({
          ...member,
          displayName,
          points: submission?.points || 0,
        });
      } else {
        missing.push({ ...member, displayName });
      }
    });

    setModalSubmittedList(submitted);
    setModalMissingList(missing);
    setModalOnLeaveList(onLeave);
    setShowMissingEntryModal(true);
  };

  // Handlers now call targeted fetch functions
  const handleApproveLeaveRequest = async (requestId) => {
    try {
      await api.put(`/api/users/leave-request/${requestId}/approve`);
      setSnackbar({
        open: true,
        message: "Leave request approved!",
        severity: "success",
      });
      await fetchLeaveAndNotifications(); // Refresh only relevant data
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error approving leave request.",
        severity: "error",
      });
    }
  };

  const handleOpenLeaveRejectDialog = (requestId) => {
    setSelectedLeaveRequestId(requestId);
    setLeaveRejectDialogOpen(true);
  };

  const handleRejectLeaveRequest = async () => {
    if (!leaveRejectionReason.trim()) {
      setSnackbar({
        open: true,
        message: "Please provide a reason for rejection.",
        severity: "warning",
      });
      return;
    }
    try {
      await api.put(
        `/api/users/leave-request/${selectedLeaveRequestId}/reject`,
        { rejectionReason: leaveRejectionReason }
      );
      setSnackbar({
        open: true,
        message: "Leave request rejected.",
        severity: "success",
      });
      setLeaveRejectDialogOpen(false);
      setLeaveRejectionReason("");
      await fetchLeaveAndNotifications();
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error rejecting leave request.",
        severity: "error",
      });
    }
  };

  const handleSubmitLeaveRequest = async () => {
    if (!leaveRequestForm.reason.trim() || !leaveRequestForm.start_date) {
      setSnackbar({
        open: true,
        message: "Reason and start date are required.",
        severity: "warning",
      });
      return;
    }
    try {
      await api.post("/api/users/me/leave-request", leaveRequestForm);
      setSnackbar({
        open: true,
        message: "Leave request submitted!",
        severity: "success",
      });
      setLeaveRequestDialogOpen(false);
      setLeaveRequestForm({ reason: "", start_date: "", end_date: "" });
      await fetchLeaveAndNotifications();
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error.response?.data?.message || "Error submitting leave request.",
        severity: "error",
      });
    }
  };

  const handleOpenLeaveRevokeDialog = (requestId) => {
    setSelectedLeaveRevokeRequestId(requestId);
    setLeaveRevokeDialogOpen(true);
  };

  const handleRevokeLeaveRequest = async () => {
    if (!leaveRevocationReason.trim()) {
      setSnackbar({
        open: true,
        message: "Please provide a reason for revocation.",
        severity: "warning",
      });
      return;
    }
    try {
      await api.put(
        `/api/users/leave-request/${selectedLeaveRevokeRequestId}/revoke`,
        { revocationReason: leaveRevocationReason }
      );
      setSnackbar({
        open: true,
        message: "Leave request revoked.",
        severity: "success",
      });
      setLeaveRevokeDialogOpen(false);
      setLeaveRevocationReason("");
      await fetchLeaveAndNotifications();
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error revoking leave request.",
        severity: "error",
      });
    }
  };

  const getTopPerformers = useMemo(() => {
    if (!zonalHeads.length) return [];
    const zonalHeadList = zonalHeads.filter(
      (head) => head.role === "zonal_head"
    );
    return [...zonalHeadList].sort(
      (a, b) =>
        getMetric(b, selectedLeaderboardMetric) -
        getMetric(a, selectedLeaderboardMetric)
    );
  }, [zonalHeads, selectedLeaderboardMetric]);

const generateCalendarDays = useCallback(
    (historyData) => {
      const daysArray = [];
      if (
        !myPerformanceStartDate ||
        isNaN(myPerformanceStartDate) ||
        !myPerformanceEndDate ||
        isNaN(myPerformanceEndDate)
      )
        return [];
      const start = new Date(
        myPerformanceStartDate.getFullYear(),
        myPerformanceStartDate.getMonth(),
        1
      );
      const finalDate = new Date(myPerformanceEndDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const blankDays = start.getDay() === 0 ? 6 : start.getDay() - 1;
      for (let i = 0; i < blankDays; i++) daysArray.push({ key: `blank-${i}` });
      let loopDate = new Date(start);
      while (loopDate <= finalDate) {
        const dateStr = formatDateForInput(loopDate);
        const holidayObj = isIndianGovernmentHoliday(loopDate, corporateHolidays);
        const isHoliday = !!holidayObj;

        let status = null;
        let holidayName = null;

        if (isHoliday) {
          status = "holiday";
          holidayName = holidayObj.name;
        } else if (historyData && historyData[dateStr] && loopDate <= today) {
          status = "submitted";
        } else if (loopDate <= today) {
          status = "missing";
        } else if (loopDate > today) {
          status = "future";
        }

        // Add Sunday highlighting (only if not a holiday)
        if (loopDate.getDay() === 0 && loopDate <= today && !isHoliday) {
          status = "sunday";
        }

        daysArray.push({
          key: dateStr,
          day: loopDate.getDate(),
          status,
          dateStr,
          holidayName,
        });
        loopDate.setDate(loopDate.getDate() + 1);
      }
      return daysArray;
    },
    [myPerformanceStartDate, myPerformanceEndDate, corporateHolidays]
  );

  const generateDirectorOrTeamCalendarDays = useCallback(
    (historyData, memberList) => {
      const daysArray = [];
      if (
        !directorCalendarStartDate ||
        !directorCalendarEndDate ||
        isNaN(directorCalendarStartDate) ||
        isNaN(directorCalendarEndDate)
      )
        return [];
      let loopDate = new Date(
        directorCalendarStartDate.getFullYear(),
        directorCalendarStartDate.getMonth(),
        1
      );
      const finalDate = new Date(directorCalendarEndDate);
      const todayZero = new Date();
      todayZero.setHours(0, 0, 0, 0);

      const blankDays = loopDate.getDay() === 0 ? 6 : loopDate.getDay() - 1;
      for (let i = 0; i < blankDays; i++) daysArray.push({ key: `blank-${i}` });

      // Use currentLevelUsers for hierarchical navigation, fallback to zonal heads for director level
      const targetGroup = memberList || currentLevelUsers;

      const submissionCounts = {};
      historyData.forEach((entry) => {
        const date = entry.date;
        if (!submissionCounts[date]) submissionCounts[date] = new Set();
        submissionCounts[date].add(Number(entry.userId));
      });

      while (loopDate <= finalDate) {
        const dateStr = formatDateForInput(loopDate);
        const isSunday = loopDate.getDay() === 0;
        const holidayObj = isIndianGovernmentHoliday(loopDate, corporateHolidays);
        const isHoliday = !!holidayObj;

        const activeMembersOnDate = targetGroup.filter(
          (m) => !isUserOnLeaveOnDate(m, dateStr)
        );
        const onLeaveCount = targetGroup.length - activeMembersOnDate.length;

        const submittedOnDate = activeMembersOnDate.filter((m) =>
          submissionCounts[dateStr]?.has(Number(m.userId))
        );

        // console.log(`📅 ${dateStr}: ${activeMembersOnDate.length} active, ${submittedOnDate.length} submitted`);

        let status = "future";
        let percentage = 0;
        let holidayName = null;

        if (loopDate <= todayZero) {
          // Calculate percentage for all days including Sundays and holidays
          percentage =
            activeMembersOnDate.length > 0
              ? Math.round(
                  (submittedOnDate.length / activeMembersOnDate.length) * 100
                )
              : 0;

          if (isHoliday) {
            status = "holiday";
            holidayName = holidayObj.name;
          } else if (onLeaveCount > 0 && onLeaveCount >= activeMembersOnDate.length) {
            status = "on_leave";
          } else if (isSunday && percentage === 0) {
            // Only show "sunday" status if no submissions on Sunday
            status = "sunday";
          } else {
            status = percentage > 0 ? "submitted" : "missing";
          }
        } else if (isHoliday) {
          status = "holiday";
          holidayName = holidayObj.name;
        }

        daysArray.push({
          key: dateStr,
          day: loopDate.getDate(),
          percentage,
          dateStr,
          status,
          holidayName,
        });
        loopDate.setDate(loopDate.getDate() + 1);
      }
      return daysArray;
    },
    [directorCalendarStartDate, directorCalendarEndDate, currentLevelUsers, corporateHolidays]
  );

  const calendarDays = useMemo(
    () => generateCalendarDays(submissionHistory),
    [generateCalendarDays, submissionHistory]
  );

  const directorPerformanceCalendarDays = useMemo(
    () =>
      generateDirectorOrTeamCalendarDays(
        directorSubmissionHistory,
        currentLevelUsers
      ),
    [
      generateDirectorOrTeamCalendarDays,
      directorSubmissionHistory,
      calendarLevel,
      currentLevelUsers,
    ]
  );

  if (isInitialLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading Dashboard...</Typography>
      </Box>
    );
  }

  if (error && currentSection === "dashboard") {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Alert severity="error">{error}</Alert>
        <Button
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
          variant="outlined"
        >
          Try Again
        </Button>
      </Box>
    );
  }

  const monthlyTargetPoints = 1000;
  const monthlyTargetProgress =
    kpis && monthlyTargetPoints > 0
      ? Math.round((kpis.aggregatedPoints / monthlyTargetPoints) * 100)
      : 0;
  const conversionRate =
    kpis && kpis.totalCalls > 0
      ? Math.round((kpis.salesClosed / kpis.totalCalls) * 100)
      : 0;
  const daysInMonth = new Date(
    new Date().getFullYear(),
    new Date().getMonth() + 1,
    0
  ).getDate();
  const avgDailyCalls =
    kpis && daysInMonth > 0 && kpis.totalCalls != null ? (kpis.totalCalls / daysInMonth).toFixed(1) : "0.0";
  const avgSalesClose =
    kpis && daysInMonth > 0 && kpis.salesClosed != null ? (kpis.salesClosed / daysInMonth).toFixed(1) : "0.0";
  const dashboardTitle = `${toTitleCase(userRole)} Dashboard`;
  const userRoleDisplay = toTitleCase(userRole);

  return (
    <div>
      {isOnLeave && (
        <div
          style={{
            background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
            color: "white",
            padding: "20px 32px",
            textAlign: "center",
            fontSize: "16px",
            fontWeight: "500",
            position: "sticky",
            top: 0,
            zIndex: 10001,
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
            borderBottom: "3px solid #ff6b35",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "16px",
            minHeight: "60px",
          }}
        >
          <div
            style={{
              backgroundColor: "rgba(255, 107, 53, 0.2)",
              borderRadius: "50%",
              padding: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "2px solid rgba(255, 255, 255, 0.3)",
            }}
          >
            <FiClock style={{ fontSize: "24px", color: "#ff6b35" }} />
          </div>
          <div style={{ textAlign: "left" }}>
            <div
              style={{
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "4px",
              }}
            >
              Currently on Leave
            </div>
            <div style={{ fontSize: "14px", opacity: "0.9" }}>
              Day {leaveDurationDays} of your leave period{" "}
              {leaveEndDate &&
                `• Returns: ${formatNotificationDate(leaveEndDate)}`}
            </div>
          </div>
        </div>
      )}
      <header className="dashboard-header">
        <div className="header-container">
          <div className="header-left">
            <div className="logo-container">
              <div className="logo">
                {" "}
                <img src={logo1} alt="Logo" />{" "}
              </div>
              <span className="logo-text">Aionion Capital Markets</span>
            </div>
            <div className="header-title-group">
              <h1>{dashboardTitle}</h1>
              <span className="performance-tag">Performance Tracking</span>
            </div>
          </div>
          <div className="header-right">
            <nav className="nav-tabs">
              <button
                className={`nav-tab ${
                  currentSection === "dashboard" ? "active" : ""
                }`}
                onClick={() => navigate("/director-dashboard/dashboard")}
              >
                <FiBarChart2 /> Dashboard
              </button>
              <button
                className="nav-tab"
                onClick={() => navigate("/director-dashboard/input")}
              >
                <FiPlus /> Input
              </button>
              <button
                className={`nav-tab ${
                  currentSection === "monthly-report" ? "active" : ""
                }`}
                onClick={() => navigate("/director-dashboard/monthly-report")}
              >
                <FiFileText /> Monthly Report
              </button>
              <button
                className={`nav-tab ${
                  currentSection === "user-management" ? "active" : ""
                }`}
                onClick={() => navigate("/director-dashboard/user-management")}
              >
                <FiUsers /> User Management
              </button>
              <button
                className={`nav-tab ${
                  currentSection === "notifications" ? "active" : ""
                }`}
                onClick={() => navigate("/director-dashboard/notifications")}
              >
                <FiBell /> Notifications
              </button>
              <button
                className={`nav-tab ${
                  currentSection === "settings" ? "active" : ""
                }`}
                onClick={() => navigate("/director-dashboard/settings")}
              >
                <FiSettings /> Settings
              </button>
            </nav>
            <div className="user-profile">
              <img
                src={user.profile_photo || defaultAvatar}
                alt="Profile"
                className="profile-picture"
                onError={(e) => {
                  e.target.src = defaultAvatar;
                }}
              />
              <div className="user-info">
                <span className="user-name">{user.name || userName}</span>
                <span className="user-role">{userRoleDisplay}</span>
              </div>
            </div>
          </div>
        </div>
      </header>
      <main className="main-content">
        {(currentSection === "input" || currentSection === "input-for") && (
          <SalesTracker
            dashboardData={dashboardData}
            onBackClick={() => navigate("/director-dashboard/dashboard")}
            onEntrySubmit={reloadDataForSection}
            userId={targetUserId}
            entryDate={entryDateForTracker}
          />
        )}
        {currentSection === "dashboard" && (
          <div className="dashboard-grid">
            <section className="card todays-status-card">
              <div className="card-header">
                <h2>Today's Status</h2>
                <div className="live-indicator">
                  {(() => {
                    const date = new Date();
                    const day = date.getDate();
                    const month = date.toLocaleString("en-GB", {
                      month: "short",
                    });
                    const year = date.getFullYear();
                    return `${day} ${month},${year}`;
                  })()}
                </div>
              </div>
              <div className="status-boxes">
                {todaysStatus.submitted ? (
                  <div className="status-box submitted">
                    <div className="status-content">
                      <h3>Submitted</h3> <p>Entry for today is complete</p>
                      <span>
                        <FiClock /> Last updated at{" "}
                        {todaysStatus.submissionTime &&
                          new Date(
                            todaysStatus.submissionTime
                          ).toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })}
                      </span>
                    </div>
                    <div className="status-icon-wrapper green">
                      <FiCheckCircle />
                    </div>
                  </div>
                ) : (
                  <div className="status-box not-submitted">
                    <div className="status-content">
                      <h3>Not Submitted</h3> <p>No entry for today yet</p>
                      <span>Click Quick Entry to submit.</span>
                    </div>
                    <div className="status-icon-wrapper grey">
                      <FiClock />
                    </div>
                  </div>
                )}
                <Tooltip
                  title={
                    <div style={{ fontSize: "14px", lineHeight: "1.6" }}>
                      <div>Avg Daily Calls Made: {monthlyKpiData?.kpis?.avgDailyCalls || "0.0"}</div>
                      <div>Avg Sales Closed: {monthlyKpiData?.kpis?.avgSalesClose || "0.0"}</div>
                      <div>
                        Total Appointments Kept:{" "}
                        {monthlyKpiData?.kpis?.appointmentsKept || 0}
                      </div>
                    </div>
                  }
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: "#ffffff",
                        color: "#000000",
                        fontSize: "14px",
                        padding: "14px 18px",
                        borderRadius: "10px",
                        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
                        minWidth: "220px",
                        lineHeight: "1.6",
                      },
                    },
                    arrow: { sx: { color: "#ffffff" } },
                  }}
                >
                  <div className="status-box quick-stats">
                    <div className="status-content">
                      <h3>Quick Stats</h3> <p>This month's summary</p>
                      <span>
                        <FiTrendingUp /> Avg daily points:{" "}
                        {kpis &&
                          Math.round(
                            (kpis.aggregatedPoints || 0) /
                              new Date(
                                new Date().getFullYear(),
                                new Date().getMonth() + 1,
                                0
                              ).getDate()
                          )}
                      </span>
                      <span>
                        <FiAward /> Top activity:{" "}
                        {kpis &&
                          (() => {
                            const activities = {
                              totalCalls: kpis.totalCalls || 0,
                              appointmentsKept: kpis.appointmentsKept || 0,
                              faceToFaceMeetings: kpis.faceToFaceMeetings || 0,
                              salesClosed: kpis.salesClosed || 0,
                            };
                            const top = Object.keys(activities).reduce((a, b) =>
                              activities[a] > activities[b] ? a : b
                            );
                            return toTitleCase(top);
                          })()}
                      </span>
                    </div>
                    <div className="status-icon-wrapper blue">
                      <FiTrendingUp />
                    </div>
                  </div>
                </Tooltip>
              </div>
            </section>
            <section className="card quick-entry-card">
              <div
                className="quick-entry-icon"
                onClick={() => navigate("/director-dashboard/input")}
              >
                <FiPlus />
              </div>
              <h2>
                {todaysStatus.submitted ? "Edit Today's Entry" : "Quick Entry"}
              </h2>
              <button
                className="submit-entry-btn"
                onClick={() => navigate("/director-dashboard/input")}
              >
                {todaysStatus.submitted ? "Edit Entry" : "Submit Today's Entry"}
              </button>
              <span className="secure-note">
                <FiLock /> Secure & Encrypted
              </span>
            </section>

            <div className="stat-card-row">
              <section className="card stat-card">
                <div className="stat-header">
                  <h4>Aggregated Points</h4>
                  <FiAward className="stat-icon trophy" />
                </div>
                <div className="stat-main">
                  <span className="stat-value">
                    {monthlyKpiData.kpis
                      ? monthlyKpiData.kpis.aggregatedPoints || 0
                      : 0}
                  </span>
                </div>
                <p className="stat-period">
                  Last month:{" "}
                  {monthlyKpiData.previousMonthKpis
                    ? monthlyKpiData.previousMonthKpis.aggregatedPoints || 0
                    : 0}
                </p>
              </section>
              <section className="card stat-card">
                <div className="stat-header">
                  <h4>Monthly Target</h4>
                  <FiTarget className="stat-icon target" />
                </div>
                <div className="stat-main">
                  <span className="stat-value">{monthlyTargetProgress}%</span>
                </div>
                <p className="stat-period">
                  Current:{" "}
                  {monthlyKpiData.kpis
                    ? monthlyKpiData.kpis.aggregatedPoints || 0
                    : 0}{" "}
                  / {monthlyTargetPoints} points
                </p>
              </section>
              <section className="card stat-card">
                <div className="stat-header">
                  <h4>New Sales Calls</h4>
                  <FiUsers className="stat-icon interactions" />
                </div>
                <div className="stat-main">
                  <span className="stat-value">
                    {monthlyKpiData.kpis
                      ? monthlyKpiData.kpis.salesInteractions || 0
                      : 0}
                  </span>
                </div>
                <p className="stat-period">
                  Last month:{" "}
                  {monthlyKpiData.previousMonthKpis
                    ? monthlyKpiData.previousMonthKpis.salesInteractions || 0
                    : 0}
                </p>
              </section>
              <section className="card stat-card">
                <div className="stat-header">
                  <h4>Avg Daily Calls</h4>
                  <FiPhoneCall
                    className="stat-icon"
                    style={{ color: "#3498db" }}
                  />
                </div>
                <div className="stat-main">
                  <span className="stat-value">
                    {monthlyKpiData.kpis
                      ? monthlyKpiData.kpis.avgDailyCalls || "0.0"
                      : "0.0"}
                  </span>
                </div>
                <p className="stat-period">
                  Last month:{" "}
                  {monthlyKpiData.previousMonthKpis
                    ? monthlyKpiData.previousMonthKpis.avgDailyCalls || "0.0"
                    : "0.0"}
                </p>
              </section>
              <section className="card stat-card">
                <div className="stat-header">
                  <h4>Avg Sales Close</h4>
                  <FiTrendingUp
                    className="stat-icon"
                    style={{ color: "#2ecc71" }}
                  />
                </div>
                <div className="stat-main">
                  <span className="stat-value">
                    {monthlyKpiData.kpis
                      ? monthlyKpiData.kpis.avgSalesClose || "0.0"
                      : "0.0"}
                  </span>
                </div>
                <p className="stat-period">
                  Last month:{" "}
                  {monthlyKpiData.previousMonthKpis
                    ? monthlyKpiData.previousMonthKpis.avgSalesClose || "0.0"
                    : "0.0"}
                </p>
              </section>
              <section className="card stat-card">
                <div className="stat-header">
                  <h4>Appts. Kept</h4>
                  <FiCheckSquare
                    className="stat-icon"
                    style={{ color: "#9b59b6" }}
                  />
                </div>
                <div className="stat-main">
                  <span className="stat-value">
                    {monthlyKpiData.kpis
                      ? monthlyKpiData.kpis.appointmentsKept || 0
                      : 0}
                  </span>
                </div>
                <p className="stat-period">
                  Last month:{" "}
                  {monthlyKpiData.previousMonthKpis
                    ? monthlyKpiData.previousMonthKpis.appointmentsKept || 0
                    : 0}
                </p>
              </section>
              <section className="card stat-card">
                <div className="stat-header">
                  <h4>Conversion Rate</h4>
                  <FiBarChart2 className="stat-icon conversion" />
                </div>
                <div className="stat-main">
                  <span className="stat-value">
                    {monthlyKpiData.kpis
                      ? monthlyKpiData.kpis.conversionRate || 0
                      : 0}
                    %
                  </span>
                </div>
                <p className="stat-period">
                  Last month:{" "}
                  {monthlyKpiData.previousMonthKpis
                    ? monthlyKpiData.previousMonthKpis.conversionRate || 0
                    : 0}
                  %
                </p>
              </section>
            </div>

            <section
              className="card zonal-details-table-card"
              style={{ gridColumn: "1 / -1" }}
            >
              <div className="card-header">
                <h2>Zonal Head Overview</h2>
                <div className="header-controls-container">
                  <div className="date-range-filters">
                    <span className="date-label">From</span>
                    <input
                      type="date"
                      className="date-input"
                      value={formatDateForInput(zonalDetailsStartDate)}
                      onChange={(e) =>
                        setZonalDetailsStartDate(
                          new Date(e.target.value + "T00:00:00")
                        )
                      }
                    />
                    <span className="date-label">To</span>
                    <input
                      type="date"
                      className="date-input"
                      value={formatDateForInput(zonalDetailsEndDate)}
                      onChange={(e) =>
                        setZonalDetailsEndDate(
                          new Date(e.target.value + "T00:00:00")
                        )
                      }
                    />
                  </div>
                  <div className="search-container">
                    <span className="search-icon">🔍</span>
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Search by name"
                      value={dashboardSearchTerm}
                      onChange={(e) => setDashboardSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
              </div>
              {isZonalDetailsLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 200,
                  }}
                >
                  <CircularProgress size={30} />
                  <Typography sx={{ ml: 2 }} color="text.secondary">
                    Updating Details...
                  </Typography>
                </Box>
              ) : (
                <TableContainer
                  component={Paper}
                  sx={{ maxHeight: 400, overflow: "auto", mt: 2 }}
                >
                  <Table stickyHeader sx={{ minWidth: 1600 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Zonal Head Name</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Director</TableCell>
                        <TableCell align="right">Calls</TableCell>
                        <TableCell align="right">F2F</TableCell>
                        <TableCell align="right">Appt. Made</TableCell>
                        <TableCell align="right">Appt. Kept</TableCell>
                        <TableCell align="right">Fact Find</TableCell>
                        <TableCell align="right">Reviews</TableCell>
                        <TableCell align="right">Social</TableCell>
                        <TableCell align="right">HIW</TableCell>
                        <TableCell align="right">Referrals</TableCell>
                        <TableCell align="right">Closing Int.</TableCell>
                        <TableCell align="right">Sales Close</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {zonalHeads
                        .filter((head) => head.role === "zonal_head")
                        .filter((head) =>
                          (head.name || head.username || "")
                            .toLowerCase()
                            .includes(dashboardSearchTerm.toLowerCase())
                        )
                        .map((head) => (
                          <TableRow
                            key={`head-details-${head.userId}`}
                            hover
                            sx={{ cursor: "pointer" }}
                            onClick={() => {
                              navigate("/director-dashboard/user-management", {
                                state: { preSelectedZonalHead: head },
                              });
                            }}
                          >
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                color: "var(--primary-purple)",
                              }}
                            >
                              {head.name || head.username}
                            </TableCell>
                            <TableCell>
                              {head.leaveStatus ? (
                                <Tooltip
                                  title={`On leave until ${formatNotificationDate(
                                    head.leaveEndDate
                                  )}`}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      color: "#D97706",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    <FiClock
                                      size={14}
                                      style={{ marginRight: 4 }}
                                    />{" "}
                                    On Leave
                                  </Box>
                                </Tooltip>
                              ) : (
                                <Box
                                  sx={{ color: "#16A34A", fontWeight: "bold" }}
                                >
                                  Active
                                </Box>
                              )}
                            </TableCell>
                            <TableCell>{head.directorName || "N/A"}</TableCell>
                            <TableCell align="right">
                              {getMetric(head, "telephoneSalesCalls")}
                            </TableCell>
                            <TableCell align="right">
                              {getMetric(head, "faceToFace")}
                            </TableCell>
                            <TableCell align="right">
                              {getMetric(head, "appointmentMade")}
                            </TableCell>
                            <TableCell align="right">
                              {getMetric(head, "appointmentKept")}
                            </TableCell>
                            <TableCell align="right">
                              {getMetric(head, "factFind")}
                            </TableCell>
                            <TableCell align="right">
                              {getMetric(head, "serviceReviews")}
                            </TableCell>
                            <TableCell align="right">
                              {getMetric(head, "socialCalls")}
                            </TableCell>
                            <TableCell align="right">
                              {getMetric(head, "highIncomeWealthy")}
                            </TableCell>
                            <TableCell align="right">
                              {getMetric(head, "referrals")}
                            </TableCell>
                            <TableCell align="right">
                              {getMetric(head, "closingInterview")}
                            </TableCell>
                            <TableCell align="right">
                              {getMetric(head, "salesClose")}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </section>

            <div className="performance-row">
              <section className="card submission-history-card">
                <div className="card-header">
                  <div className="card-title-group">
                    <h2>My Submission History</h2>
                  </div>
                  <div className="card-controls">
                    <div className="date-range-filters">
                      <span className="date-label">From</span>
                      <input
                        type="date"
                        className="date-input"
                        value={formatDateForInput(myPerformanceStartDate)}
                        onChange={(e) =>
                          setMyPerformanceStartDate(
                            new Date(e.target.value + "T00:00:00")
                          )
                        }
                      />
                      <span className="date-label">To</span>
                      <input
                        type="date"
                        className="date-input"
                        value={formatDateForInput(myPerformanceEndDate)}
                        onChange={(e) =>
                          setMyPerformanceEndDate(
                            new Date(e.target.value + "T00:00:00")
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
                {isMyPerfLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 200,
                    }}
                  >
                    <CircularProgress size={30} />
                  </Box>
                ) : (
                  <div className="calendar-grid-wrapper">
                    <div className="calendar-sticky-header">
                      {["M", "T", "W", "T", "F", "S", "S"].map((day, idx) => (
                        <div key={idx} className="calendar-day-header">
                          {day}
                        </div>
                      ))}
                    </div>
                <div className="calendar-grid">
                  {calendarDays.map((dayInfo) => {
                    if (!dayInfo.day)
                      return (
                        <div
                          key={`submission-blank-${dayInfo.key}`}
                          className="calendar-day"
                        ></div>
                      );
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const entryDateObj = new Date(
                      dayInfo.dateStr.replace(/-/g, "/")
                    );
                    const daysDiff = Math.floor(
                      (today - entryDateObj) / (1000 * 60 * 60 * 24)
                    );
                    const isClickable = entryDateObj <= today;
                    const isEditable = daysDiff >= 0 && daysDiff <= 2;
                    let className = `calendar-day ${dayInfo.status || ""}`;
                    if (isClickable) className += " clickable";
                        return (
                          <Tooltip
                            key={`submission-${dayInfo.key}`}
                            title={dayInfo.status === "holiday" && dayInfo.holidayName ? dayInfo.holidayName : ""}
                            arrow
                            placement="top"
                          >
                            <div
                              className={className}
                              onClick={() =>
                                isClickable &&
                                navigate(
                                  `/director-dashboard/input/${dayInfo.dateStr}`
                                )
                              }
                            >
                              {dayInfo.status === "submitted" && <FiCheckCircle />}
                              {dayInfo.status === "missing" && (
                                <span className="missing-icon">&times;</span>
                              )}
                              {dayInfo.status === "holiday" && (
                                <span className="holiday-indicator">🏖️</span>
                              )}
                              {dayInfo.status && !isEditable && daysDiff > 2 && (
                                <FiLock className="lock-icon" />
                              )}
                              <span>{dayInfo.day}</span>
                            </div>
                          </Tooltip>
                        );
                  })}
                </div>
                  </div>
                )}
                <div className="calendar-legend">
                  <div className="legend-item">
                    <span className="legend-dot submitted"></span>Submitted
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot missing"></span>Missing
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot on_leave"></span>On Leave
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot sunday"></span>Sunday
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot holiday"></span>Holiday
                  </div>
                </div>
              </section>
              <section className="card my-performance-card">
                <div className="card-header">
                  <div className="card-title-group">
                    <h2>Performance Analytics</h2>
                  </div>
                  <div className="card-controls single-row">
                    <div className="control-group">
                      <span className="control-label">From</span>
                      <input
                        type="date"
                        className="date-input"
                        value={formatDateForInput(myPerformanceStartDate)}
                        onChange={(e) =>
                          setMyPerformanceStartDate(
                            new Date(e.target.value + "T00:00:00")
                          )
                        }
                      />
                    </div>
                    <div className="control-group">
                      <span className="control-label">To</span>
                      <input
                        type="date"
                        className="date-input"
                        value={formatDateForInput(myPerformanceEndDate)}
                        onChange={(e) =>
                          setMyPerformanceEndDate(
                            new Date(e.target.value + "T00:00:00")
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
                {isMyPerfLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <CircularProgress size={30} />
                  </Box>
                ) : (
                  <>
                    <div
                      className="field-filters"
                      style={{
                        justifyContent: "center",
                        gap: "16px",
                        display: "flex",
                        marginBottom: "16px",
                      }}
                    >
                      <div className="field-filter">
                        <label htmlFor="field-select1">Metric 1:</label>
                        <select
                          id="field-select1"
                          value={selectedField1}
                          onChange={(e) => setSelectedField1(e.target.value)}
                          className="field-select"
                        >
                          {chartFieldOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="field-filter">
                        <label htmlFor="field-select2">Metric 2:</label>
                        <select
                          id="field-select2"
                          value={selectedField2}
                          onChange={(e) => setSelectedField2(e.target.value)}
                          className="field-select"
                        >
                          {chartFieldOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="chart-legend">
                      <div className="legend-item">
                        <span
                          className="legend-dot"
                          style={{ backgroundColor: "var(--primary-blue)" }}
                        ></span>
                        {toTitleCase(selectedField1)}
                      </div>
                      {selectedField2 !== "none" && (
                        <div className="legend-item">
                          <span
                            className="legend-dot"
                            style={{ backgroundColor: "var(--green)" }}
                          ></span>
                          {toTitleCase(selectedField2)}
                        </div>
                      )}
                    </div>
                    <div className="chart-container">
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart
                          data={performanceTrend}
                          margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            strokeOpacity={0.2}
                          />
                          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                          <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
                          {selectedField2 !== "none" && (
                            <YAxis
                              yAxisId="right"
                              orientation="right"
                              tick={{ fontSize: 10 }}
                            />
                          )}
                          <ChartTooltip
                            wrapperStyle={{ zIndex: 9999 }}
                            contentStyle={{
                              backgroundColor: "var(--card-bg, #ffffff)",
                              border: "1px solid var(--border-color, #e0e0e0)",
                              borderRadius: "8px",
                              boxShadow: "var(--card-shadow)",
                            }}
                          />
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey={selectedField1}
                            stroke="var(--primary-blue)"
                            strokeWidth={2}
                            dot={{ r: 4 }}
                            activeDot={{ r: 6 }}
                            name={toTitleCase(selectedField1)}
                          />
                          {selectedField2 !== "none" && (
                            <Line
                              yAxisId="right"
                              type="monotone"
                              dataKey={selectedField2}
                              stroke="var(--green)"
                              strokeWidth={2}
                              dot={{ r: 4 }}
                              activeDot={{ r: 6 }}
                              name={toTitleCase(selectedField2)}
                            />
                          )}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </>
                )}
              </section>
            </div>

            <div className="team-performance-row">
              <section className="card director-performance-calendar-card">
                <div className="card-header">
                  <div className="card-title-group">
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      {/* Breadcrumb Navigation */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          fontSize: "14px",
                          color: "#666",
                          flexWrap: "wrap",
                        }}
                      >
                        <Tooltip title="Click to return to main director view">
                          <span
                            style={{
                              cursor: "pointer",
                              color: "#1976d2",
                              fontWeight: "500",
                            }}
                            onClick={() => {
                              setCalendarLevel("director");
                              setSelectedUserId(null);
                              setSelectedUserName("");
                              setNavigationHistory([]);
                            }}
                          >
                            📊 Director
                          </span>
                        </Tooltip>
                        {navigationHistory.map((entry, index) => (
                          <React.Fragment key={index}>
                            <span style={{ color: "#999" }}>→</span>
                            <Tooltip
                              title={
                                entry.level === "zonal_head"
                                  ? `Click to view ${entry.userName}'s Branch Managers`
                                  : entry.level === "branch_manager"
                                  ? `Click to view ${entry.userName}'s Team Leads`
                                  : `Click to view ${entry.userName}'s Relationship Managers`
                              }
                            >
                              <span
                                style={{
                                  cursor:
                                    index < navigationHistory.length - 1
                                      ? "pointer"
                                      : "default",
                                  color:
                                    index < navigationHistory.length - 1
                                      ? "#1976d2"
                                      : "#000",
                                  fontWeight:
                                    index === navigationHistory.length - 1
                                      ? "bold"
                                      : "normal",
                                  backgroundColor:
                                    index === navigationHistory.length - 1
                                      ? "#f0f8ff"
                                      : "transparent",
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                }}
                                onClick={() => {
                                  if (index < navigationHistory.length - 1) {
                                    // Navigate to this level
                                    const newHistory = navigationHistory.slice(
                                      0,
                                      index + 1
                                    );
                                    setNavigationHistory(newHistory);
                                    const targetEntry =
                                      newHistory[newHistory.length - 1];
                                    setCalendarLevel(targetEntry.level);
                                    setSelectedUserId(targetEntry.userId);
                                    setSelectedUserName(targetEntry.userName);
                                  }
                                }}
                              >
                                {entry.level === "zonal_head" &&
                                  `🏢 ${entry.userName}`}
                                {entry.level === "branch_manager" &&
                                  `👔 ${entry.userName}`}
                                {entry.level === "team_lead" &&
                                  `👥 ${entry.userName}`}
                              </span>
                            </Tooltip>
                          </React.Fragment>
                        ))}
                      </div>

                      {/* Calendar Title */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          flexWrap: "wrap",
                        }}
                      >
                        {navigationHistory.length > 0 && (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={navigateBack}
                            startIcon={<FiChevronLeft />}
                            sx={{ minWidth: "auto", px: 1 }}
                          >
                            Back
                          </Button>
                        )}
                        <h2>
                          {calendarLevel === "director" &&
                            "Director Team Calendar"}
                          {calendarLevel === "zonal_head" &&
                            `${selectedUserName} Branch Managers Calendar`}
                          {calendarLevel === "branch_manager" &&
                            `${selectedUserName} Team Leads Calendar`}
                          {calendarLevel === "team_lead" &&
                            `${selectedUserName} RMs Calendar`}
                        </h2>
                        {calendarLevel === "director" &&
                          navigationHistory.length === 0 && (
                            <Tooltip title="💡 Tip: Click 'Explore Zonal Heads' to drill down into team performance">
                              <Box
                                sx={{
                                  backgroundColor: "#e3f2fd",
                                  color: "#1976d2",
                                  padding: "4px 8px",
                                  borderRadius: "4px",
                                  fontSize: "12px",
                                  fontWeight: "500",
                                  cursor: "help",
                                }}
                              >
                                How to navigate →
                              </Box>
                            </Tooltip>
                          )}
                      </div>
                    </div>
                    {calendarLevel === "director" && (
                      <Tooltip title="Open a quick view of your entire team hierarchy">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={async () => {
                            setTeamExplorerMode("list");
                            setTeamExplorerContext("calendar");
                            setTeamExplorerZonalHead(null);
                            setTeamExplorerData(null);
                            setShowTeamExplorerModal(true);
                            await fetchDirectorTeamOverview();
                          }}
                          startIcon={<FiUsers />}
                        >
                          Explore Team
                        </Button>
                      </Tooltip>
                    )}
                    {calendarLevel === "zonal_head" && (
                      <Tooltip title="Click to see Branch Managers under this Zonal Head and drill down further">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setShowCurrentLevelUsersModal(true)}
                          startIcon={<FiUsers />}
                        >
                          Explore Branch Managers
                        </Button>
                      </Tooltip>
                    )}
                    {calendarLevel === "branch_manager" && (
                      <Tooltip title="Click to see Team Leads under this Branch Manager and drill down further">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setShowCurrentLevelUsersModal(true)}
                          startIcon={<FiUsers />}
                        >
                          Explore Team Leads
                        </Button>
                      </Tooltip>
                    )}
                    {calendarLevel === "team_lead" && (
                      <Tooltip title="Click to see Relationship Managers under this Team Lead">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setShowCurrentLevelUsersModal(true)}
                          startIcon={<FiUsers />}
                        >
                          View Relationship Managers
                        </Button>
                      </Tooltip>
                    )}
                  </div>
                  <div className="card-controls">
                    <div className="date-range-filters">
                      <span className="date-label">From</span>
                      <input
                        type="date"
                        className="date-input"
                        value={formatDateForInput(directorCalendarStartDate)}
                        onChange={(e) =>
                          setDirectorCalendarStartDate(
                            new Date(e.target.value + "T00:00:00")
                          )
                        }
                      />
                      <span className="date-label">To</span>
                      <input
                        type="date"
                        className="date-input"
                        value={formatDateForInput(directorCalendarEndDate)}
                        onChange={(e) =>
                          setDirectorCalendarEndDate(
                            new Date(e.target.value + "T00:00:00")
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
                {isDirectorCalendarLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 200,
                    }}
                  >
                    <CircularProgress size={30} />
                  </Box>
                ) : (
                  <div className="calendar-grid-wrapper">
                    <div className="calendar-sticky-header">
                      {["M", "T", "W", "T", "F", "S", "S"].map((day, idx) => (
                        <div key={idx} className="calendar-day-header">
                          {day}
                        </div>
                      ))}
                    </div>
                    <div className="calendar-grid">
                      {directorPerformanceCalendarDays.map((dayInfo) => (
                        <Tooltip
                          key={dayInfo.key}
                          title={dayInfo.status === "holiday" && dayInfo.holidayName ? dayInfo.holidayName : ""}
                          arrow
                          placement="top"
                        >
                          <div
                            className={`calendar-day ${dayInfo.status || ""} ${
                              dayInfo.day && dayInfo.status !== "future"
                                ? "clickable"
                                : ""
                            }`}
                            onClick={() =>
                              dayInfo.day &&
                              dayInfo.status &&
                              dayInfo.status !== "future" &&
                              handleCalendarDateClick(dayInfo.dateStr)
                            }
                          >
                            {dayInfo.day ? (
                              <>
                                {" "}
                                {dayInfo.status === "submitted" && (
                                  <FiCheckCircle />
                                )}{" "}
                                {dayInfo.status === "holiday" && (
                                  <span className="holiday-indicator">🏖️</span>
                                )}{" "}
                                {dayInfo.status !== "future" && (
                                  <span className="points">
                                    {dayInfo.individualStatus
                                      ? // Show individual status for single person views
                                        dayInfo.individualStatus === "submitted"
                                        ? "✓"
                                        : dayInfo.individualStatus === "missing"
                                        ? "✗"
                                        : dayInfo.individualStatus ===
                                          "both_submitted"
                                        ? "✓✓"
                                        : dayInfo.individualStatus ===
                                          "one_submitted"
                                        ? "✓✗"
                                        : dayInfo.individualStatus ===
                                          "none_submitted"
                                        ? "✗✗"
                                        : dayInfo.percentage + "%"
                                      : // Show percentage for group views
                                        dayInfo.percentage + "%"}
                                  </span>
                                )}{" "}
                                <span>{dayInfo.day}</span>{" "}
                              </>
                            ) : (
                              <>&nbsp;</>
                            )}
                          </div>
                        </Tooltip>
                      ))}
                    </div>
                  </div>
                )}
                <div className="calendar-legend">
                  <div className="legend-item">
                    <span className="legend-dot submitted"></span>Submitted
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot missing"></span>Missing
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot on_leave"></span>On Leave
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot sunday"></span>Sunday
                  </div>
                </div>
              </section>

              <section className="card team-performance-card">
                <div className="card-header">
                  <div className="card-title-group">
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      {/* Breadcrumb Navigation */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          fontSize: "14px",
                          color: "#666",
                          flexWrap: "wrap",
                        }}
                      >
                        <Tooltip title="Click to return to main director view">
                          <span
                            style={{
                              cursor: "pointer",
                              color: "#1976d2",
                              fontWeight: "500",
                            }}
                          onClick={() => {
                            setChartLevel("director");
                            setChartSelectedUserId(null);
                            setChartSelectedUserName("");
                            setChartSelectedBranchId(null);
                            setChartBranchManagerMode("default");
                            setChartBranchManagerDirectRMs([]);
                            setChartNavigationHistory([]);
                          }}
                          >
                            📊 Director
                          </span>
                        </Tooltip>
                        {chartNavigationHistory.map((entry, index) => (
                          <React.Fragment key={index}>
                            <span style={{ color: "#999" }}>→</span>
                            <Tooltip
                              title={
                                entry.level === "zonal_head"
                                  ? `Click to view ${entry.userName}'s Branch Managers`
                                  : entry.level === "branch_manager"
                                  ? `Click to view ${entry.userName}'s Team Leads`
                                  : `Click to view ${entry.userName}'s Relationship Managers`
                              }
                            >
                              <span
                                style={{
                                  cursor:
                                    index < chartNavigationHistory.length - 1
                                      ? "pointer"
                                      : "default",
                                  color:
                                    index < chartNavigationHistory.length - 1
                                      ? "#1976d2"
                                      : "#000",
                                  fontWeight:
                                    index === chartNavigationHistory.length - 1
                                      ? "bold"
                                      : "normal",
                                  backgroundColor:
                                    index === chartNavigationHistory.length - 1
                                      ? "#f0f8ff"
                                      : "transparent",
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                }}
                                onClick={() => {
                                  if (
                                    index <
                                    chartNavigationHistory.length - 1
                                  ) {
                                    // Navigate to this level
                                    const newHistory =
                                      chartNavigationHistory.slice(
                                        0,
                                        index + 1
                                      );
                                    setChartNavigationHistory(newHistory);
                                    const targetEntry =
                                      newHistory[newHistory.length - 1];
                                    setChartLevel(targetEntry.level);
                                    setChartSelectedUserId(targetEntry.userId);
                                    setChartSelectedUserName(
                                      targetEntry.userName
                                    );
                                    setChartSelectedBranchId(
                                      targetEntry.level === "branch_manager"
                                        ? targetEntry.branchId || null
                                        : null
                                    );
                                    if (targetEntry.level !== "branch_manager") {
                                      setChartBranchManagerMode("default");
                                      setChartBranchManagerDirectRMs([]);
                                    }
                                  }
                                }}
                              >
                                {entry.level === "zonal_head" &&
                                  `🏢 ${entry.userName}`}
                                {entry.level === "branch_manager" &&
                                  `👔 ${entry.userName}`}
                                {entry.level === "team_lead" &&
                                  `👥 ${entry.userName}`}
                              </span>
                            </Tooltip>
                          </React.Fragment>
                        ))}
                      </div>

                      {/* Chart Title */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          flexWrap: "wrap",
                        }}
                      >
                        {chartNavigationHistory.length > 0 && (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={navigateChartBack}
                            startIcon={<FiChevronLeft />}
                            sx={{ minWidth: "auto", px: 1 }}
                          >
                            Back
                          </Button>
                        )}
                        <h2>
                          {chartLevel === "director" &&
                            "Director Team Charts"}
                          {chartLevel === "zonal_head" &&
                            (chartZonalMode === "direct_rm"
                              ? `${chartSelectedUserName} RMs Performance`
                              : `${chartSelectedUserName} Branch Managers Performance`)}
                          {chartLevel === "branch_manager" &&
                            (chartBranchManagerMode === "direct_rm"
                              ? `${chartSelectedUserName} RMs Performance`
                              : `${chartSelectedUserName} Team Leads Performance`)}
                          {chartLevel === "team_lead" &&
                            `${chartSelectedUserName} RMs Performance`}
                        </h2>
                        {chartLevel === "director" &&
                          chartNavigationHistory.length === 0 && (
                            <Tooltip title="💡 Tip: Click 'Explore Zonal Heads' to drill down into team performance">
                              <Box
                                sx={{
                                  backgroundColor: "#e3f2fd",
                                  color: "#1976d2",
                                  padding: "4px 8px",
                                  borderRadius: "4px",
                                  fontSize: "12px",
                                  fontWeight: "500",
                                  cursor: "help",
                                }}
                              >
                                How to navigate →
                              </Box>
                            </Tooltip>
                          )}
                      </div>
                    </div>
                    {chartLevel === "director" && (
                      <Tooltip title="Click to see all Zonal Heads and drill down into their teams">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={openChartExploreModal}
                          startIcon={<FiUsers />}
                        >
                          Explore Team Charts
                        </Button>
                      </Tooltip>
                    )}
                    {chartLevel === "zonal_head" && (
                      <Tooltip title="Click to see Branch Managers under this Zonal Head and drill down further">
                        <Button
                          variant="outlined"
                          size="small"
                            onClick={() =>
                              setShowChartCurrentLevelUsersModal(true)
                            }
                          startIcon={<FiUsers />}
                        >
                          Explore Branch Managers
                        </Button>
                      </Tooltip>
                    )}
                    {chartLevel === "branch_manager" &&
                      chartBranchManagerMode !== "direct_rm" && (
                      <Tooltip title="Click to see Team Leads under this Branch Manager and drill down further">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() =>
                            setShowChartCurrentLevelUsersModal(true)
                          }
                          startIcon={<FiUsers />}
                        >
                          Explore Team Leads
                        </Button>
                      </Tooltip>
                    )}
                    {chartLevel === "team_lead" && (
                      <Tooltip title="Click to see Relationship Managers under this Team Lead">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() =>
                            setShowChartCurrentLevelUsersModal(true)
                          }
                          startIcon={<FiUsers />}
                        >
                          View Relationship Managers
                        </Button>
                      </Tooltip>
                    )}
                  </div>
                  <div className="card-controls single-row">
                    <div className="control-group">
                      <span className="control-label">Metric:</span>
                      <select
                        id="team-field-select"
                        value={selectedPerfMetric}
                        onChange={(e) => setSelectedPerfMetric(e.target.value)}
                        className="field-select"
                      >
                        {performanceChartMetrics.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="control-group">
                      <span className="control-label">From</span>
                      <input
                        type="date"
                        className="date-input"
                        value={formatDateForInput(directorPerformanceStartDate)}
                        onChange={(e) =>
                          setDirectorPerformanceStartDate(
                            new Date(e.target.value + "T00:00:00")
                          )
                        }
                      />
                    </div>
                    <div className="control-group">
                      <span className="control-label">To</span>
                      <input
                        type="date"
                        className="date-input"
                        value={formatDateForInput(directorPerformanceEndDate)}
                        onChange={(e) =>
                          setDirectorPerformanceEndDate(
                            new Date(e.target.value + "T00:00:00")
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
                {isDirectorPerfLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <CircularProgress size={30} />
                  </Box>
                ) : chartData && chartData.length > 0 ? (
                  <div className="chart-container">
                    <ResponsiveContainer width="100%" height={360}>
                      <BarChart
                        data={chartData}
                        margin={{ top: 5, right: 20, left: 10, bottom: 50 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          strokeOpacity={0.2}
                        />
                        <XAxis
                          dataKey="displayName"
                          interval={0}
                          tick={{ fontSize: 12, angle: -35, textAnchor: "end" }}
                        />
                        <YAxis />
                        <ChartTooltip
                          cursor={{ fill: "var(--faint-bg, #f4f7fa)" }}
                          wrapperStyle={{ zIndex: 9999 }}
                          contentStyle={{
                            backgroundColor: "var(--card-bg, #ffffff)",
                            border: "1px solid var(--border-color, #e0e0e0)",
                            borderRadius: "8px",
                            boxShadow: "var(--card-shadow)",
                          }}
                        />
                        <Bar
                          dataKey={selectedPerfMetric}
                          name={
                            performanceChartMetrics.find(
                              (opt) => opt.value === selectedPerfMetric
                            )?.label || selectedPerfMetric
                          }
                          fill="#4A90E2"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div
                    className="chart-container"
                    style={{
                      height: "300px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      padding: "20px",
                      textAlign: "center",
                    }}
                  >
                    <Typography color="textSecondary" gutterBottom>
                      No performance data available for the selected range.
                    </Typography>
                  </div>
                )}
              </section>
            </div>

            <section className="card leaderboard-card">
              <div className="card-header">
                <h2>Zonal Head Rankings</h2>
                <div className="card-controls">
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <Select
                      value={selectedLeaderboardMetric}
                      onChange={(e) =>
                        setSelectedLeaderboardMetric(e.target.value)
                      }
                      displayEmpty
                    >
                      {leaderboardFieldOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </div>
              {isZonalDetailsLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 150,
                  }}
                >
                  <CircularProgress size={30} />
                </Box>
              ) : (
                <div
                  className="leaderboard-list"
                  style={
                    getTopPerformers.length > 3
                      ? { maxHeight: "300px", overflowY: "auto" }
                      : {}
                  }
                >
                  {getTopPerformers.length > 0 ? (
                    getTopPerformers.map((head, index) => (
                      <div
                        key={`leaderboard-${head.userId}`}
                        className="leaderboard-item"
                      >
                        <span className="rank">{index + 1}</span>
                        <span className="name">
                          {head.name || head.username}
                        </span>
                        <span className="points">
                          {getMetric(head, selectedLeaderboardMetric) || 0}{" "}
                          {leaderboardFieldOptions
                            .find(
                              (opt) => opt.value === selectedLeaderboardMetric
                            )
                            ?.label.includes("Points")
                            ? "pts"
                            : ""}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div
                      style={{
                        padding: "20px",
                        textAlign: "center",
                        color: "#666",
                      }}
                    >
                      <Typography>
                        No zonal heads found or no performance data available
                      </Typography>
                    </div>
                  )}
                </div>
              )}
              {getTopPerformers.length > 0 &&
                getTopPerformers.every(
                  (head) => getMetric(head, selectedLeaderboardMetric) === 0
                ) && (
                  <div
                    style={{
                      padding: "8px 16px",
                      fontSize: "12px",
                      color: "#666",
                      borderTop: "1px solid #f0f0f0",
                    }}
                  >
                    <strong>Note:</strong> Zonal Heads are ranked, but all have
                    zero values for the selected metric.
                  </div>
                )}
            </section>
          </div>
        )}

        {currentSection === "monthly-report" && <MonthlyReport />}
        {currentSection === "user-management" && <DirectorUserManagement />}
        {currentSection === "settings" && (
          <ProfilePage
            profile={user}
            profilePhotoUrl={user.profile_photo}
            onProfileUpdate={fetchInitialStaticData}
          />
        )}
        {currentSection === "notifications" && (
          <div className="notifications-section">
            <h2>Notifications & Approvals</h2>
            <div className="notifications-tabs">
              <button
                className={`tab-button ${
                  selectedNotificationTab === "pending-approvals"
                    ? "active"
                    : ""
                }`}
                onClick={() => setSelectedNotificationTab("pending-approvals")}
              >
                Pending Approvals ({filteredPendingLeaveRequests.length})
              </button>
              <button
                className={`tab-button ${
                  selectedNotificationTab === "approved-requests"
                    ? "active"
                    : ""
                }`}
                onClick={() => setSelectedNotificationTab("approved-requests")}
              >
                Staff on Leave ({approvedLeaveRequests.length})
              </button>
              <button
                className={`tab-button ${
                  selectedNotificationTab === "director-submissions"
                    ? "active"
                    : ""
                }`}
                onClick={() =>
                  setSelectedNotificationTab("director-submissions")
                }
              >
                Director Submissions
              </button>
              <button
                className={`tab-button ${
                  selectedNotificationTab === "my-requests" ? "active" : ""
                }`}
                onClick={() => setSelectedNotificationTab("my-requests")}
              >
                My Leave Requests
              </button>
              <button
                className={`tab-button ${
                  selectedNotificationTab === "all-notifications"
                    ? "active"
                    : ""
                }`}
                onClick={() => setSelectedNotificationTab("all-notifications")}
              >
                All Notifications
              </button>
            </div>
            {selectedNotificationTab === "my-requests" && (
              <div className="pending-requests-list">
                <h3>My Leave Requests</h3>
                {ownLeaveRequests.length > 0 ? (
                  ownLeaveRequests.map((request) => (
                    <div
                      key={request.leaveRequestId}
                      className="leave-request-item"
                    >
                      <p>
                        <strong>Your</strong> leave request from{" "}
                        <strong>
                          {formatNotificationDate(request.startDate)}
                        </strong>
                        {request.endDate ? (
                          <>
                            {" to "}
                            <strong>
                              {formatNotificationDate(request.endDate)}
                            </strong>
                          </>
                        ) : (
                          ""
                        )}
                        .
                      </p>
                      <p>
                        <strong>Reason:</strong> {request.reason}
                      </p>
                      <div className="status-badge">
                        <span className={`status ${request.status}`}>
                          {toTitleCase(request.status)}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <Typography color="textSecondary">
                      No personal leave requests found.
                    </Typography>
                  </div>
                )}
              </div>
            )}
            {selectedNotificationTab === "director-submissions" && (
              <div className="director-submissions-list">
                <h3>Recent Director Entry Submissions (Last 7 Days)</h3>
                {directorSubmissions.length > 0 ? (
                  directorSubmissions.map((submission, index) => (
                    <div
                      key={`submission-item-${index}`}
                      className="submission-item"
                    >
                      <span className="submission-indicator submitted" />
                      <div className="submission-info">
                        <Typography variant="body1">
                          <strong>{submission.rmName}</strong> submitted entry
                          for{" "}
                          <strong>
                            {formatNotificationDate(submission.date)}
                          </strong>
                          .
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {submission.points} points •{" "}
                          {submission.telephonesalescalls} calls •{" "}
                          {submission.salesclose} sales
                        </Typography>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <Typography color="textSecondary">
                      No recent submissions from your team members.
                    </Typography>
                  </div>
                )}
              </div>
            )}
            {selectedNotificationTab === "pending-approvals" && (
              <div className="pending-approvals-list">
                <h3>Leave Requests Pending Approval</h3>
                {filteredPendingLeaveRequests.length > 0 ? (
                  filteredPendingLeaveRequests.map((request) => {
                    const requestorDetails = zonalHeadsMap.get(request.userId);
                    const managerName = requestorDetails?.name || "N/A";
                    return (
                      <div
                        key={`pending-${request.leaveRequestId}`}
                        className="leave-request-item"
                      >
                        <div className="request-header">
                          <Typography variant="body1">
                            <span className="role-badge">
                              {toTitleCase(request.requestorRole)}
                            </span>{" "}
                            <strong>{request.requestorName}</strong> has
                            requested leave from{" "}
                            <strong>
                              {formatNotificationDate(request.startDate)}
                            </strong>
                            {request.endDate && (
                              <>
                                {" to "}
                                <strong>
                                  {formatNotificationDate(request.endDate)}
                                </strong>
                              </>
                            )}
                            .
                          </Typography>
                          <div className="request-actions">
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() =>
                                handleApproveLeaveRequest(
                                  request.leaveRequestId
                                )
                              }
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() =>
                                handleOpenLeaveRejectDialog(
                                  request.leaveRequestId
                                )
                              }
                            >
                              Reject
                            </Button>
                          </div>
                        </div>
                        <Typography variant="body2">
                          <strong>Reason:</strong> {request.reason}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Requested on:{" "}
                          {formatNotificationDate(request.requestedAt)}
                        </Typography>
                      </div>
                    );
                  })
                ) : (
                  <div className="empty-state">
                    <FiCheckCircle size={40} style={{ color: "#ccc" }} />
                    <Typography color="textSecondary" sx={{ mt: 2 }}>
                      No pending leave requests from your subordinates.
                    </Typography>
                  </div>
                )}
              </div>
            )}
            {selectedNotificationTab === "approved-requests" && (
              <div className="approved-requests-list">
                <h3>Staff on Leave</h3>
                {approvedLeaveRequests.length > 0 ? (
                  approvedLeaveRequests.map((request) => (
                    <div
                      key={`approved-${request.leaveRequestId}`}
                      className="leave-request-item"
                    >
                      <div className="request-header">
                        <Typography variant="body1">
                          <span className="role-badge">
                            {toTitleCase(request.requestorRole)}
                          </span>{" "}
                          <strong>{request.requestorName}</strong> is on leave
                          from{" "}
                          <strong>
                            {formatNotificationDate(request.startDate)}
                          </strong>
                          {request.endDate && (
                            <>
                              {" to "}
                              <strong>
                                {formatNotificationDate(request.endDate)}
                              </strong>
                            </>
                          )}
                          .
                        </Typography>
                        <div className="request-actions">
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={() =>
                              handleOpenLeaveRevokeDialog(
                                request.leaveRequestId
                              )
                            }
                          >
                            Revoke
                          </Button>
                        </div>
                      </div>
                      <Typography variant="body2">
                        <strong>Reason:</strong> {request.reason}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Approved on:{" "}
                        {formatNotificationDate(request.approvedAt)}
                      </Typography>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <Typography color="textSecondary">
                      No staff currently on leave.
                    </Typography>
                  </div>
                )}
              </div>
            )}
            {selectedNotificationTab === "all-notifications" && (
              <div className="notifications-list">
                <h3>All Notifications</h3>
                {notifications.length > 0 ? (
                  notifications.map((notification) => (
                    <div key={notification.id} className="notification-item">
                      <div className="notification-content">
                        <Typography variant="body1">
                          {notification.message}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {formatNotificationDate(notification.date)}
                        </Typography>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <Typography color="textSecondary">
                      No notifications available.
                    </Typography>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </main>

      <Dialog
        open={showMissingEntryModal}
        onClose={() => setShowMissingEntryModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{modalTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            ✅ Submitted ({modalSubmittedList.length}):
          </Typography>
          {modalSubmittedList.length > 0 ? (
            <TableContainer component={Paper} sx={{ mt: 1, mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Points</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                      {modalSubmittedList.map((item) => (
                        <TableRow key={`submitted-item-${item.userId}`}>
                          <TableCell>{item.displayName || labelWithRole(item)}</TableCell>
                          <TableCell align="right">{item.points || 0}</TableCell>
                        </TableRow>
                      ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ mt: 1, mb: 3 }}
            >
              None.
            </Typography>
          )}
          <Typography variant="h6" gutterBottom>
            🟨 On Leave ({modalOnLeaveList.length}):
          </Typography>
          {modalOnLeaveList.length > 0 ? (
            <TableContainer component={Paper} sx={{ mt: 1, mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {modalOnLeaveList.map((item) => (
                    <TableRow key={`onleave-item-${item.userId}`}>
                      <TableCell>{item.displayName || labelWithRole(item)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ mt: 1, mb: 3 }}
            >
              None.
            </Typography>
          )}
          <Typography variant="h6" gutterBottom>
            ❌ Missing ({modalMissingList.length}):
          </Typography>
          {modalMissingList.length > 0 ? (
            <TableContainer component={Paper} sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {modalMissingList.map((item, index) => (
                    <TableRow
                      key={`missing-item-${item.userId || item.id || index}`}
                    >
                      <TableCell>{item.displayName || labelWithRole(item)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              All other active members have submitted.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMissingEntryModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={leaveRequestDialogOpen}
        onClose={() => setLeaveRequestDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Request Leave</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Submit a leave request.
          </Typography>
          <TextField
            label="Reason"
            value={leaveRequestForm.reason}
            onChange={(e) =>
              setLeaveRequestForm({
                ...leaveRequestForm,
                reason: e.target.value,
              })
            }
            fullWidth
            required
            margin="normal"
            multiline
            rows={3}
            variant="outlined"
          />
          <TextField
            label="Start Date"
            type="date"
            value={leaveRequestForm.start_date}
            onChange={(e) =>
              setLeaveRequestForm({
                ...leaveRequestForm,
                start_date: e.target.value,
              })
            }
            fullWidth
            required
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Date"
            type="date"
            value={leaveRequestForm.end_date}
            onChange={(e) =>
              setLeaveRequestForm({
                ...leaveRequestForm,
                end_date: e.target.value,
              })
            }
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveRequestDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitLeaveRequest}
            variant="contained"
            color="primary"
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={leaveRejectDialogOpen}
        onClose={() => setLeaveRejectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Leave Request</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Please provide a reason for rejecting the leave request.
          </Typography>
          <TextField
            label="Rejection Reason"
            value={leaveRejectionReason}
            onChange={(e) => setLeaveRejectionReason(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={4}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveRejectDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleRejectLeaveRequest}
            variant="contained"
            color="error"
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={leaveRevokeDialogOpen}
        onClose={() => setLeaveRevokeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Revoke Leave Approval</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Please provide a reason for revoking the approved leave request.
          </Typography>
          <TextField
            label="Revocation Reason"
            value={leaveRevocationReason}
            onChange={(e) => setLeaveRevocationReason(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={4}
            variant="outlined"
            placeholder="e.g., Urgent project requirements, Business needs changed, etc."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveRevokeDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleRevokeLeaveRequest}
            variant="contained"
            color="error"
          >
            Revoke Approval
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showZonalHeadsModal}
        onClose={() => setShowZonalHeadsModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Zonal Heads under {user.name || user.username}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
            Select a Zonal Head to view their Branch Managers and submission
            calendar.
          </Typography>
          {zonalHeads.filter((head) => head.role === "zonal_head").length >
          0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {zonalHeads
                    .filter((head) => head.role === "zonal_head")
                    .map((head) => (
                      <TableRow key={`zh-${head.userId}`}>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 600 }}
                            >
                              {head.name || head.username}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {head.leaveStatus ? (
                            <Box sx={{ color: "#D97706", fontWeight: "bold" }}>
                              On Leave
                            </Box>
                          ) : (
                            <Box sx={{ color: "#16A34A", fontWeight: "bold" }}>
                              Active
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              navigateToLevel(
                                "zonal_head",
                                head.userId,
                                head.name || head.username
                              );
                              setShowZonalHeadsModal(false);
                            }}
                          >
                            View Branch Managers
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="textSecondary">No Zonal Heads found.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowZonalHeadsModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showTeamExplorerModal}
        onClose={() => setShowTeamExplorerModal(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {(teamExplorerMode === "detail" ||
                teamExplorerMode === "bm_detail") && (
                <Button
                  startIcon={<FiChevronLeft />}
                  onClick={() => {
                    setTeamExplorerMode("list");
                    setTeamExplorerZonalHead(null);
                    setTeamExplorerData(null);
                    setSelectedBranchManagerDetail(null);
                  }}
                >
                  Back
                </Button>
              )}
              {teamExplorerMode === "detail"
                ? `Teams under ${teamExplorerZonalHead?.name || ""}`
                : teamExplorerMode === "bm_detail"
                ? `Teams under ${selectedBranchManagerDetail?.name || ""}`
                : "Explore Team"}
            </Box>
            {teamExplorerMode === "detail" && isChartExplorer && (
              <Button
                variant="contained"
                size="small"
                onClick={handleViewZonalHeadTeamCharts}
                disabled={isTeamExplorerLoading}
              >
                View Zonal Head Team Charts
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {teamExplorerMode === "list" && (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Quick view of everyone directly under you: zonal heads, branch
                managers, team leads, and direct RMs.
              </Typography>
              {isDirectorTeamOverviewLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Zonal Heads
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(260px, 1fr))",
                        gap: 2,
                      }}
                    >
                      {(directorTeamOverview.zonalHeads || []).map((head) => (
                        <Paper
                          key={`team-exp-${head.userId}`}
                          sx={{
                            p: 2,
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            height: "100%",
                          }}
                          elevation={2}
                        >
                          <Typography variant="h6">
                            {head.name || head.username}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Zonal Head
                          </Typography>
                          <Button
                            variant="contained"
                            startIcon={<FiUsers />}
                            onClick={() => handleOpenZonalTeamModal(head)}
                            disabled={isTeamExplorerLoading}
                          >
                            {isTeamExplorerLoading
                              ? "Loading..."
                              : labelWithContextSuffix("View Team")}
                          </Button>
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Direct Branch Managers
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(260px, 1fr))",
                        gap: 2,
                      }}
                    >
                      {(directorTeamOverview.branchManagers || []).map((bm) => (
                        <Paper
                          key={`bm-dir-${bm.userId}`}
                          elevation={2}
                          sx={{
                            p: 2,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Box>
                            <Typography variant="subtitle1" fontWeight={700}>
                              {bm.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                            >
                              Branch Manager
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={async () => {
                                if (isChartExplorer) {
                                  setChartBranchManagerMode("default");
                                  setChartBranchManagerDirectRMs([]);
                                  setShowTeamExplorerModal(false);
                                  goToLevel(
                                    "branch_manager",
                                    bm.userId,
                                    bm.name,
                                    bm.id || bm.branchId || bm.branch_id || null
                                  );
                                  return;
                                }

                                try {
                                  const tlRes = await api.get(
                                    `/api/users/team-leads-by-branch-manager?branchManagerId=${bm.userId}`
                                  );
                                  setCurrentLevelUsers(
                                    addDisplayNames(tlRes.data || [])
                                  );
                                  // Preload submission history for these TLs
                                  const subsRes = await api.get(
                                    `/api/users/branch-detailed-submission-history?startDate=${formatDateForInput(
                                      directorCalendarStartDate
                                    )}&endDate=${formatDateForInput(
                                      directorCalendarEndDate
                                    )}&branchManagerId=${bm.userId}`
                                  );
                                  const transformedData = (subsRes.data || []).map(
                                    (entry) => ({
                                      userId: entry.userId,
                                      date: entry.date,
                                      points: entry.points,
                                    })
                                  );
                                  setDirectorSubmissionHistory(transformedData);
                                } catch (error) {
                                  console.error("Failed to load team leads", error);
                                }
                                setShowTeamExplorerModal(false);
                                goToLevel("branch_manager", bm.userId, bm.name);
                              }}
                            >
                              {labelWithContextSuffix("View Team Lead")}
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={async () => {
                                if (isChartExplorer) {
                                  setIsTeamExplorerLoading(true);
                                  try {
                                    const rmRes = await api.get(
                                      `/api/users/relationship-managers-by-director?branchManagerId=${bm.userId}`
                                    );
                                    const directRMs = (rmRes.data || [])
                                      .map((rm) => ({
                                        ...rm,
                                        userId: rm.userId || rm.user_id || rm.id,
                                      }))
                                      .filter(
                                        (rm) =>
                                          (rm.reportsToRole ||
                                            rm.reports_to_role ||
                                            rm.reportsTo ||
                                            "") === "branch_manager"
                                      );

                                    const normalizedRMs = addDisplayNames(directRMs);
                                    setChartBranchManagerDirectRMs(normalizedRMs);
                                    setChartBranchManagerMode("direct_rm");
                                    setChartSelectedBranchId(
                                      bm.id || bm.branchId || bm.branch_id || null
                                    );
                                    setChartSelectedUserId(bm.userId);
                                    setChartSelectedUserName(bm.name);
                                    setChartLevel("branch_manager");
                                    setChartNavigationHistory((prev) => [
                                      ...prev,
                                      {
                                        level: "branch_manager",
                                        userId: bm.userId,
                                        userName: bm.name,
                                        branchId:
                                          bm.id ||
                                          bm.branchId ||
                                          bm.branch_id ||
                                          null,
                                      },
                                    ]);
                                    setChartData(transformPerformanceData(normalizedRMs));
                                    setShowTeamExplorerModal(false);
                                  } catch (error) {
                                    console.error(
                                      "Failed to load branch manager direct RM charts",
                                      error
                                    );
                                    setSnackbar({
                                      open: true,
                                      message:
                                        "Failed to load relationship managers",
                                      severity: "error",
                                    });
                                  } finally {
                                    setIsTeamExplorerLoading(false);
                                  }
                                  return;
                                }

                                setIsTeamExplorerLoading(true);
                                try {
                                  // Only direct RMs of this branch manager
                                  const rmRes = await api.get(
                                    `/api/users/relationship-managers-by-director?branchManagerId=${bm.userId}`
                                  );
                                  const directRMs = (rmRes.data || [])
                                    .map((rm) => ({
                                      ...rm,
                                      userId: rm.userId || rm.user_id || rm.id,
                                    }))
                                    // Keep only branch_manager reports
                                    .filter(
                                      (rm) =>
                                        (rm.reportsToRole ||
                                          rm.reports_to_role ||
                                          rm.reportsTo ||
                                          "") === "branch_manager"
                                    );
                                  setBranchManagerDirectRMs(directRMs);
                                  setCurrentLevelUsers(addDisplayNames(directRMs));
                                  setCalendarLevel("bm_direct_rm");
                                  setSelectedUserId(bm.userId);
                                  setSelectedUserName(bm.name);
                                  setShowTeamExplorerModal(false);
                                  setNavigationHistory((prev) => [
                                    ...prev,
                                    {
                                      level: "bm_direct_rm",
                                      userId: bm.userId,
                                      userName: bm.name,
                                    },
                                  ]);
                                  // trigger submission fetch
                                  fetchCurrentLevelSubmissionHistory();
                                } catch (error) {
                                  console.error(
                                    "Failed to load branch manager direct RMs",
                                    error
                                  );
                                  setSnackbar({
                                    open: true,
                                    message:
                                      "Failed to load direct relationship managers",
                                    severity: "error",
                                  });
                                } finally {
                                  setIsTeamExplorerLoading(false);
                                }
                              }}
                            >
                              {labelWithContextSuffix("View RM")}
                            </Button>
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Direct Team Leads
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(240px, 1fr))",
                        gap: 2,
                      }}
                    >
                      {(directorTeamOverview.teamLeads || []).map((tl) => (
                        <Paper
                          key={`tl-dir-${tl.userId}`}
                          elevation={2}
                          sx={{
                            p: 2,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Box>
                            <Typography variant="subtitle1" fontWeight={700}>
                              {tl.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                            >
                              Team Lead
                            </Typography>
                          </Box>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => {
                              if (isChartExplorer) {
                                goToLevel("team_lead", tl.userId, tl.name);
                                setShowTeamExplorerModal(false);
                                return;
                              }

                              // Clear stale data before loading RM view for this team lead
                              setCurrentLevelUsers([]);
                              setDirectorSubmissionHistory([]);
                              goToLevel("team_lead", tl.userId, tl.name);
                              setShowTeamExplorerModal(false);
                            }}
                          >
                            {labelWithContextSuffix(
                              "View Relationship Managers"
                            )}
                          </Button>
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Direct Relationship Managers
                    </Typography>
                    {(directorTeamOverview.relationshipManagers || []).length >
                    0 ? (
                      <Button
                        variant="contained"
                        startIcon={<FiUsers />}
                        onClick={handleViewDirectRMsForDirector}
                      >
                        {labelWithContextSuffix("View Direct RMs")}
                      </Button>
                    ) : (
                      <Typography color="textSecondary">
                        No direct relationship managers.
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            </>
          )}
          {teamExplorerMode === "detail" && (
            <>
              {isTeamExplorerLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                  <CircularProgress size={28} />
                </Box>
              )}
              {!isTeamExplorerLoading && teamExplorerData && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Branch Manager Teams
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(280px, 1fr))",
                        gap: 2,
                      }}
                    >
                      {(teamExplorerData.branchManagers || []).map((bm) => {
                        const tls =
                          (teamExplorerData.teamLeadsUnderBMs || []).filter(
                            (tl) =>
                              Number(tl.branchManagerUserId) ===
                              Number(bm.userId)
                          ) || [];
                        return (
                          <Paper
                            key={`bm-card-${bm.userId}`}
                            elevation={3}
                            sx={{
                              p: 2,
                              display: "flex",
                              flexDirection: "column",
                              gap: 1.5,
                            }}
                          >
                            <Typography variant="subtitle1" fontWeight={700}>
                              {bm.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              sx={{ mb: 1 }}
                            >
                              Branch Manager
                            </Typography>
                            {tls.length > 0 && (
                              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                {tls.map((tl) => (
                                  <Box
                                    key={`tl-chip-${tl.userId}`}
                                    sx={{
                                      px: 1.5,
                                      py: 0.5,
                                      borderRadius: "12px",
                                      border: "1px solid #ccc",
                                      fontSize: "12px",
                                      background: "#f8f8ff",
                                    }}
                                  >
                                    {tl.name} (team lead)
                                  </Box>
                                ))}
                              </Box>
                            )}
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => {
                                  goToLevel(
                                    "branch_manager",
                                    bm.userId,
                                    bm.name
                                  );
                                  setShowTeamExplorerModal(false);
                                }}
                              >
                                View Team
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={async () => {
                                  if (isChartExplorer) {
                                    setIsTeamExplorerLoading(true);
                                    try {
                                      const rmRes = await api.get(
                                        `/api/users/relationship-managers-by-director?branchManagerId=${bm.userId}`
                                      );
                                      const directRMs = (rmRes.data || [])
                                        .map((rm) => ({
                                          ...rm,
                                          userId: rm.userId || rm.user_id || rm.id,
                                        }))
                                        .filter(
                                          (rm) =>
                                            (rm.reportsToRole ||
                                              rm.reports_to_role ||
                                              rm.reportsTo ||
                                              "") === "branch_manager"
                                        );
                                      const normalizedRMs = addDisplayNames(directRMs);
                                      setChartBranchManagerDirectRMs(normalizedRMs);
                                      setChartBranchManagerMode("direct_rm");
                                      setChartSelectedBranchId(
                                        bm.id || bm.branchId || bm.branch_id || null
                                      );
                                      setChartSelectedUserId(bm.userId);
                                      setChartSelectedUserName(bm.name);
                                      setChartLevel("branch_manager");
                                      setChartNavigationHistory((prev) => [
                                        ...prev,
                                        {
                                          level: "branch_manager",
                                          userId: bm.userId,
                                          userName: bm.name,
                                          branchId:
                                            bm.id ||
                                            bm.branchId ||
                                            bm.branch_id ||
                                            null,
                                        },
                                      ]);
                                      setChartData(transformPerformanceData(normalizedRMs));
                                      setShowTeamExplorerModal(false);
                                    } catch (error) {
                                      console.error(
                                        "Failed to load branch manager direct RM charts",
                                        error
                                      );
                                      setSnackbar({
                                        open: true,
                                        message:
                                          "Failed to load relationship managers",
                                        severity: "error",
                                      });
                                    } finally {
                                      setIsTeamExplorerLoading(false);
                                    }
                                    return;
                                  }

                                  setIsTeamExplorerLoading(true);
                                  try {
                                    const rmRes = await api.get(
                                      `/api/users/relationship-managers-by-director?branchManagerId=${bm.userId}`
                                    );
                                    const directRMs = (rmRes.data || [])
                                      .map((rm) => ({
                                        ...rm,
                                        userId: rm.userId || rm.user_id || rm.id,
                                      }))
                                      .filter(
                                        (rm) =>
                                          (rm.reportsToRole ||
                                            rm.reports_to_role ||
                                            rm.reportsTo ||
                                            "") === "branch_manager"
                                      );

                                    setBranchManagerDirectRMs(directRMs);
                                    setCurrentLevelUsers(
                                      addDisplayNames(directRMs)
                                    );
                                    setCalendarLevel("bm_direct_rm");
                                    setSelectedUserId(bm.userId);
                                    setSelectedUserName(bm.name);
                                    setShowTeamExplorerModal(false);
                                    setNavigationHistory((prev) => [
                                      ...prev,
                                      {
                                        level: "bm_direct_rm",
                                        userId: bm.userId,
                                        userName: bm.name,
                                      },
                                    ]);

                                    // Preload submission history for these direct RMs
                                    const endpoint = `/api/users/director-detailed-submission-history?startDate=${formatDateForInput(
                                      directorCalendarStartDate
                                    )}&endDate=${formatDateForInput(
                                      directorCalendarEndDate
                                    )}&branchManagerId=${
                                      bm.userId
                                    }&directRMs=true`;
                                    const response = await api.get(endpoint);
                                    const transformedData = (
                                      response.data || []
                                    ).map((entry) => ({
                                      userId: entry.userId,
                                      date: entry.date,
                                      points: entry.points,
                                    }));
                                    setDirectorSubmissionHistory(
                                      transformedData
                                    );
                                  } catch (error) {
                                    console.error(
                                      "Failed to load branch manager direct RMs",
                                      error
                                    );
                                    setSnackbar({
                                      open: true,
                                      message:
                                        "Failed to load relationship managers",
                                      severity: "error",
                                    });
                                  } finally {
                                    setIsTeamExplorerLoading(false);
                                  }
                                }}
                              >
                                View RM
                              </Button>
                            </Box>
                          </Paper>
                        );
                      })}
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Direct Team Lead Teams
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(260px, 1fr))",
                        gap: 2,
                      }}
                    >
                      {(teamExplorerData.directTeamLeads || []).map((tl) => (
                        <Paper
                          key={`dtl-${tl.userId}`}
                          elevation={2}
                          sx={{
                            p: 2,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Box>
                            <Typography variant="subtitle1" fontWeight={700}>
                              {tl.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                            >
                              Team Lead
                            </Typography>
                          </Box>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={async () => {
                              if (isChartExplorer) {
                                goToLevel("team_lead", tl.userId, tl.name);
                                setShowTeamExplorerModal(false);
                                return;
                              }

                              setIsTeamExplorerLoading(true);
                              try {
                                // Fetch RMs assigned to this team lead
                                const rmRes = await api.get(
                                  `/api/users/rms-by-team-lead/${tl.userId}`
                                );
                                const rms = addDisplayNames(rmRes.data || []);

                                // Preload submission history for these RMs
                                const endpoint = `/api/users/branch-detailed-submission-history?startDate=${formatDateForInput(
                                  directorCalendarStartDate
                                )}&endDate=${formatDateForInput(
                                  directorCalendarEndDate
                                )}&teamLeadId=${tl.userId}`;
                                const historyRes = await api.get(endpoint);
                                const transformedData = (
                                  historyRes.data || []
                                ).map((entry) => ({
                                  userId: entry.userId,
                                  date: entry.date,
                                  points: entry.points,
                                }));

                                setCurrentLevelUsers(rms);
                                setDirectorSubmissionHistory(transformedData);
                                setCalendarLevel("team_lead");
                                setSelectedUserId(tl.userId);
                                setSelectedUserName(tl.name);
                                setNavigationHistory((prev) => [
                                  ...prev,
                                  {
                                    level: "team_lead",
                                    userId: tl.userId,
                                    userName: tl.name,
                                  },
                                ]);
                                setShowTeamExplorerModal(false);
                              } catch (error) {
                                console.error(
                                  "Failed to load team lead RMs",
                                  error
                                );
                                setSnackbar({
                                  open: true,
                                  message:
                                    "Failed to load relationship managers",
                                  severity: "error",
                                });
                              } finally {
                                setIsTeamExplorerLoading(false);
                              }
                            }}
                          >
                            View RM Team
                          </Button>
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Direct Relationship Managers
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {(teamExplorerData.directRMs || []).length > 0 ? (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleViewAllRMsForZonal}
                        >
                          View All RMs
                        </Button>
                      ) : (
                        <Typography color="textSecondary">
                          No direct relationship managers.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              )}
            </>
          )}
          {teamExplorerMode === "bm_detail" && (
            <>
              {isTeamExplorerLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                  <CircularProgress size={28} />
                </Box>
              )}
              {!isTeamExplorerLoading && selectedBranchManagerDetail && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Team Lead Teams
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(260px, 1fr))",
                        gap: 2,
                      }}
                    >
                      {(selectedBranchManagerDetail.teamLeads || []).map(
                        (tl) => (
                          <Paper
                            key={`bm-tl-${tl.userId}`}
                            elevation={2}
                            sx={{
                              p: 2,
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Box>
                              <Typography variant="subtitle1" fontWeight={700}>
                                {tl.name}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="textSecondary"
                              >
                                Team Lead
                              </Typography>
                            </Box>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => {
                                goToLevel("team_lead", tl.userId, tl.name);
                                setShowTeamExplorerModal(false);
                              }}
                            >
                              View Team
                            </Button>
                          </Paper>
                        )
                      )}
                      {(selectedBranchManagerDetail.teamLeads || []).length ===
                        0 && (
                        <Typography color="textSecondary">
                          No team leads found.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Direct Relationship Managers
                    </Typography>
                    {(selectedBranchManagerDetail.directRMs || []).length > 0 ? (
                      <Button
                        variant="contained"
                        startIcon={<FiUsers />}
                        onClick={() => {
                          if (isChartExplorer) {
                            const normalizedRMs = addDisplayNames(
                              selectedBranchManagerDetail.directRMs || []
                            );
                            setChartBranchManagerDirectRMs(normalizedRMs);
                            setChartBranchManagerMode("direct_rm");
                            setChartSelectedBranchId(
                              selectedBranchManagerDetail.branchId ||
                                selectedBranchManagerDetail.id ||
                                null
                            );
                            setChartSelectedUserId(
                              selectedBranchManagerDetail.userId
                            );
                            setChartSelectedUserName(
                              selectedBranchManagerDetail.name
                            );
                            setChartLevel("branch_manager");
                            setChartNavigationHistory((prev) => [
                              ...prev,
                              {
                                level: "branch_manager",
                                userId: selectedBranchManagerDetail.userId,
                                userName: selectedBranchManagerDetail.name,
                                branchId:
                                  selectedBranchManagerDetail.branchId ||
                                  selectedBranchManagerDetail.id ||
                                  null,
                              },
                            ]);
                            setChartData(transformPerformanceData(normalizedRMs));
                            setShowTeamExplorerModal(false);
                            return;
                          }

                          setBranchManagerDirectRMs(
                            selectedBranchManagerDetail.directRMs || []
                          );
                          setCalendarLevel("bm_direct_rm");
                          setSelectedUserId(selectedBranchManagerDetail.userId);
                          setSelectedUserName(selectedBranchManagerDetail.name);
                          setCurrentLevelUsers(
                            addDisplayNames(
                              selectedBranchManagerDetail.directRMs || []
                            )
                          );
                          setShowTeamExplorerModal(false);
                          setNavigationHistory((prev) => [
                            ...prev,
                            {
                              level: "bm_direct_rm",
                              userId: selectedBranchManagerDetail.userId,
                              userName: selectedBranchManagerDetail.name,
                            },
                          ]);
                        }}
                      >
                        View RM
                      </Button>
                    ) : (
                      <Typography color="textSecondary">
                        No direct relationship managers.
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTeamExplorerModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showCurrentLevelUsersModal}
        onClose={() => setShowCurrentLevelUsersModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {calendarLevel === "zonal_head" &&
            `Branch Managers under ${selectedUserName}`}
          {calendarLevel === "branch_manager" &&
            `Team Leads under ${selectedUserName}`}
          {calendarLevel === "team_lead" &&
            `Relationship Managers under ${selectedUserName}`}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
            {calendarLevel === "zonal_head" &&
              "Select a Branch Manager to view their Team Leads and submission calendar."}
            {calendarLevel === "branch_manager" &&
              "Select a Team Lead to view their Relationship Managers and submission calendar."}
            {calendarLevel === "team_lead" &&
              "View Relationship Managers and their submission details."}
          </Typography>
          {currentLevelUsers.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Status</TableCell>
                    {(calendarLevel === "zonal_head" ||
                      calendarLevel === "branch_manager") && (
                      <TableCell>Actions</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentLevelUsers.map((member, index) => (
                    <TableRow
                      key={`level-${member.userId || member.id || index}`}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {member.name || member.username}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {member.leaveStatus ? (
                          <Box sx={{ color: "#D97706", fontWeight: "bold" }}>
                            On Leave
                          </Box>
                        ) : (
                          <Box sx={{ color: "#16A34A", fontWeight: "bold" }}>
                            Active
                          </Box>
                        )}
                      </TableCell>
                      {(calendarLevel === "zonal_head" ||
                        calendarLevel === "branch_manager") && (
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              if (calendarLevel === "zonal_head") {
                                navigateToLevel(
                                  "branch_manager",
                                  member.userId,
                                  member.name || member.username
                                );
                              } else if (calendarLevel === "branch_manager") {
                                navigateToLevel(
                                  "team_lead",
                                  member.userId,
                                  member.name || member.username
                                );
                              }
                              setShowCurrentLevelUsersModal(false);
                            }}
                          >
                            {calendarLevel === "zonal_head"
                              ? "View Team Leads"
                              : "View RMs"}
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="textSecondary">
              {calendarLevel === "zonal_head" && "No Branch Managers found."}
              {calendarLevel === "branch_manager" && "No Team Leads found."}
              {calendarLevel === "team_lead" &&
                "No Relationship Managers found."}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCurrentLevelUsersModal(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showChartCurrentLevelUsersModal}
        onClose={() => setShowChartCurrentLevelUsersModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {chartLevel === "director" &&
            `Zonal Heads under ${user.name || user.username}`}
          {chartLevel === "zonal_head" &&
            `Branch Managers under ${chartSelectedUserName}`}
          {chartLevel === "branch_manager" &&
            `Team Leads under ${chartSelectedUserName}`}
          {chartLevel === "team_lead" &&
            `Relationship Managers under ${chartSelectedUserName}`}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
            {chartLevel === "director" &&
              "Select a Zonal Head to view their Branch Managers performance chart."}
            {chartLevel === "zonal_head" &&
              "Select a Branch Manager to view their Team Leads performance chart."}
            {chartLevel === "branch_manager" &&
              "Select a Team Lead to view their Relationship Managers performance chart."}
            {chartLevel === "team_lead" &&
              "View Relationship Managers performance details."}
          </Typography>
          {chartCurrentLevelUsers.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Status</TableCell>
                    {(chartLevel === "director" ||
                      chartLevel === "zonal_head" ||
                      chartLevel === "branch_manager") && (
                      <TableCell>Actions</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {chartCurrentLevelUsers.map((member, index) => (
                    <TableRow
                      key={`chart-level-${member.userId || member.id || index}`}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {member.name || member.username}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {member.leaveStatus ? (
                          <Box sx={{ color: "#D97706", fontWeight: "bold" }}>
                            On Leave
                          </Box>
                        ) : (
                          <Box sx={{ color: "#16A34A", fontWeight: "bold" }}>
                            Active
                          </Box>
                        )}
                      </TableCell>
                      {(chartLevel === "director" ||
                        chartLevel === "zonal_head" ||
                        chartLevel === "branch_manager") && (
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              if (chartLevel === "director") {
                                navigateChartToLevel(
                                  "zonal_head",
                                  member.userId,
                                  member.name || member.username
                                );
                              } else if (chartLevel === "zonal_head") {
                                navigateChartToLevel(
                                  "branch_manager",
                                  member.userId,
                                  member.name || member.username,
                                  member.id || member.branchId || member.branch_id || null
                                );
                              } else if (chartLevel === "branch_manager") {
                                navigateChartToLevel(
                                  "team_lead",
                                  member.userId,
                                  member.name || member.username
                                );
                              }
                              setShowChartCurrentLevelUsersModal(false);
                            }}
                          >
                            {chartLevel === "director"
                              ? "View Branch Managers"
                              : chartLevel === "zonal_head"
                              ? "View Team Leads"
                              : "View RMs"}
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="textSecondary">
              {chartLevel === "director" && "No Zonal Heads found."}
              {chartLevel === "zonal_head" && "No Branch Managers found."}
              {chartLevel === "branch_manager" && "No Team Leads found."}
              {chartLevel === "team_lead" && "No Relationship Managers found."}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowChartCurrentLevelUsersModal(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={zonalTeamModalOpen}
        onClose={() => setZonalTeamModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {zonalTeamModalData.zonalHead?.name || "Zonal Head"} - Direct Team
        </DialogTitle>
        <DialogContent dividers>
          {zonalTeamModalLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Branch Managers
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: 2,
                  }}
                >
                  {(zonalTeamModalData.branchManagers || []).map((bm) => (
                    <Paper key={`zh-bm-${bm.userId}`} sx={{ p: 2 }} elevation={2}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {bm.name || bm.username}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Branch Manager
                      </Typography>
                    </Paper>
                  ))}
                  {(zonalTeamModalData.branchManagers || []).length === 0 && (
                    <Typography color="textSecondary">
                      No branch managers found.
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Direct Team Leads
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: 2,
                  }}
                >
                  {(zonalTeamModalData.teamLeads || []).map((tl) => (
                    <Paper key={`zh-tl-${tl.userId}`} sx={{ p: 2 }} elevation={2}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {tl.name || tl.username}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Team Lead
                      </Typography>
                    </Paper>
                  ))}
                  {(zonalTeamModalData.teamLeads || []).length === 0 && (
                    <Typography color="textSecondary">
                      No direct team leads.
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Direct Relationship Managers
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: 2,
                  }}
                >
                  {(zonalTeamModalData.relationshipManagers || []).map((rm) => (
                    <Paper key={`zh-rm-${rm.userId}`} sx={{ p: 2 }} elevation={2}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {rm.name || rm.username}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Relationship Manager
                      </Typography>
                    </Paper>
                  ))}
                  {(zonalTeamModalData.relationshipManagers || []).length === 0 && (
                    <Typography color="textSecondary">
                      No direct relationship managers.
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setZonalTeamModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default DirectorDashboard;
